/*
 * RUX Theme - Reorganized Custom JS
 * 
 * Modular structure for easier maintenance and debugging
 * Version: 2.0 - Reorganized
 */

(function () {
  'use strict';

  // ============================================================================
  // GLOBAL STATE & CONFIGURATION
  // ============================================================================

  if (!window.RUXTheme) {
    window.RUXTheme = {
      initialized: false,
      handlers: new Map(),
      debug: window.location.search.includes('debug=true'),
      cartSystem: null,
      config: {
        blackColor: 'Black',
        bronzeColor: 'Bronze',
        marsRedColor: 'Mars Red',
        pressSealLid: 'Press-seal',
        triFoldLid: 'Tri-fold'
      }
    };
  }

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const RUXUtils = {
    /**
     * Debounce function calls
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Throttle function calls
     */
    throttle(func, limit) {
      let inThrottle;
      return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    /**
     * Safe element query with error handling
     */
    safeQuery(selector, context = document) {
      try {
        return context.querySelector(selector);
      } catch (error) {
        console.error(`Invalid selector: ${selector}`, error);
        return null;
      }
    },

    /**
     * Safe element query all with error handling
     */
    safeQueryAll(selector, context = document) {
      try {
        return context.querySelectorAll(selector);
      } catch (error) {
        console.error(`Invalid selector: ${selector}`, error);
        return [];
      }
    },

    /**
     * Log with debug prefix
     */
    log(message, ...args) {
      if (window.RUXTheme.debug) {
        console.log(`[RUX Debug] ${message}`, ...args);
      }
    },

    /**
     * Format price based on currency
     */
    formatPrice(priceInCents, currencyCode = 'USD') {
      try {
        const price = priceInCents / 100;
        const symbols = {
          'USD': '$', 'CAD': '$', 'EUR': '€', 'GBP': '£',
          'CHF': 'CHF ', 'SEK': 'KR ', 'DKK': 'DKK '
        };
        const symbol = symbols[currencyCode] || '';
        return currencyCode === 'USD' ? `${symbol}${price.toLocaleString()}` : `${symbol}${price.toLocaleString()} ${currencyCode}`;
      } catch (error) {
        console.error('Error formatting price:', error);
        return '';
      }
    }
  };

  // ============================================================================
  // CART SYSTEM - Enhanced with better error handling
  // ============================================================================

  class RUXCartSystem {
    constructor() {
      // Singleton pattern - prevent multiple instances
      if (RUXCartSystem.instance) {
        RUXUtils.log('CartSystem already exists, returning existing instance');
        return RUXCartSystem.instance;
      }

      this.cartDrawer = RUXUtils.safeQuery('#drawer-cart') || RUXUtils.safeQuery('cart-drawer');
      this.cartNotification = RUXUtils.safeQuery('cart-notification');
      this.eventListeners = new Map();

      // Use global processing lock to prevent duplicates across all instances
      if (!window.RUXTheme.cartProcessing) {
        window.RUXTheme.cartProcessing = false;
      }

      this.init();
      RUXCartSystem.instance = this;
    }

    // ------------------------------------------------------------------------
    // INITIALIZATION
    // ------------------------------------------------------------------------

    init() {
      try {
        this.setupTemporaryCartListener();
        this.bindEvents();
        RUXUtils.log('Cart System initialized successfully');
      } catch (error) {
        console.error('Cart System initialization failed:', error);
      }
    }

    // ------------------------------------------------------------------------
    // EVENT LISTENERS & HANDLERS
    // ------------------------------------------------------------------------

    setupTemporaryCartListener() {
      let cartListener = null;

      const clickHandler = (event) => {
        // Ignore bundle buttons - they're handled separately
        const bundleButton = event.target.closest('[data-add-bundle]');
        if (bundleButton) {
          return;
        }

        const addButton = event.target.closest('[data-add-to-cart], button[type="submit"]');
        const form = addButton?.closest('form[action*="/cart/add"]');

        if (addButton && form) {
          RUXUtils.log('Add to cart clicked - setting up temporary listener');

          if (cartListener) {
            document.removeEventListener('theme:cart:change', cartListener);
          }

          cartListener = () => {
            RUXUtils.log('Cart changed after button click - opening drawer');
            document.removeEventListener('theme:cart:change', cartListener);
            cartListener = null;

            setTimeout(() => {
              this.openDrawerViaToggle();
            }, 300);
          };

          document.addEventListener('theme:cart:change', cartListener);

          setTimeout(() => {
            if (cartListener) {
              document.removeEventListener('theme:cart:change', cartListener);
              cartListener = null;
              RUXUtils.log('Temporary cart listener expired');
            }
          }, 3000);
        }
      };

      document.addEventListener('click', clickHandler, { capture: true });
      this.eventListeners.set('temporaryCart', clickHandler);
    }

    bindEvents() {
      // Prevent double-binding by removing old listeners first
      if (this.eventListeners.has('cartActions')) {
        RUXUtils.log('Removing old event listeners before rebinding');
        const oldClickHandler = this.eventListeners.get('cartActions');
        const oldSubmitHandler = this.eventListeners.get('formSubmit');
        const oldCartChangeHandler = this.eventListeners.get('cartChange');

        if (oldClickHandler) document.removeEventListener('click', oldClickHandler, { capture: true });
        if (oldSubmitHandler) document.removeEventListener('submit', oldSubmitHandler, { capture: true });
        if (oldCartChangeHandler) document.removeEventListener('theme:cart:change', oldCartChangeHandler);
      }

      const clickHandler = this.handleCartActions.bind(this);
      const submitHandler = this.handleFormSubmit.bind(this);
      const cartChangeHandler = this.handleCartChange.bind(this);

      // Use capture phase for bundle buttons to intercept BEFORE theme handlers
      document.addEventListener('click', clickHandler, { passive: false, capture: true });
      document.addEventListener('submit', submitHandler, { capture: true });
      document.addEventListener('theme:cart:change', cartChangeHandler);

      this.eventListeners.set('cartActions', clickHandler);
      this.eventListeners.set('formSubmit', submitHandler);
      this.eventListeners.set('cartChange', cartChangeHandler);

      this.setupDrawerCloseHandling();
    }

    handleCartActions(event) {
      const bundleButton = event.target.closest('[data-add-bundle]');

      if (bundleButton) {
        RUXUtils.log('Handling bundle button click');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.addBundleToCart(event);
        return false;
      }

      const singleButton = event.target.closest('[data-add-to-cart]');
      if (singleButton && !bundleButton) {
        RUXUtils.log('Letting theme handle single product');
        return;
      }
    }

    handleFormSubmit(event) {
      const form = event.target;

      if (form.querySelector('[data-add-bundle]')) {
        RUXUtils.log('Preventing bundle form submission - click handler will manage');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Don't call addBundleToCart here - the click handler already did
        // Just prevent the form from submitting
        return false;
      }

      RUXUtils.log('Letting theme handle single product form');
    }

    handleCartChange(event) {
      const pageUrl = window.location.href;
      const isRuxTripleThreat = pageUrl.includes("rux-triple-threat");

      if (isRuxTripleThreat) {
        setTimeout(() => {
          const cartDrawer = RUXUtils.safeQuery("#rebuy-cart");
          const buttonBuy = cartDrawer?.querySelector('button[alt="Buy 3 save 10%"]');
          if (buttonBuy) {
            buttonBuy.click();
          }
        }, 1000);
      }
    }

    setupDrawerCloseHandling() {
      if (this.cartDrawer) {
        const closeButton = this.cartDrawer.querySelector('[data-drawer-close]') ||
          this.cartDrawer.querySelector('.drawer__close') ||
          this.cartDrawer.querySelector('[aria-label*="close" i]');

        if (closeButton && window.RUXTheme.debug) {
          RUXUtils.log('Cart close button found:', closeButton);
        }
      }
    }

    // ------------------------------------------------------------------------
    // CART OPERATIONS
    // ------------------------------------------------------------------------

    async addBundleToCart(event) {
      // Use global processing lock to prevent duplicates
      if (window.RUXTheme.cartProcessing) {
        RUXUtils.log('Already processing, ignoring bundle add request');
        return;
      }

      window.RUXTheme.cartProcessing = true;
      const button = event.target.closest('[data-add-bundle]');

      try {
        this.setButtonState(button, 'loading');

        // Update variant IDs before adding
        if (window.updateVariantIds && typeof window.updateVariantIds === 'function') {
          window.updateVariantIds();
        }

        const items = this.collectBundleItems(event);

        if (!items || items.length === 0) {
          throw new Error('No valid items found in bundle. Please select all required options.');
        }

        RUXUtils.log('Adding bundle items:', items);
        const cartData = await this.addToCart(items);

        if (!cartData) {
          throw new Error('Failed to receive cart data from server');
        }

        // Theme will handle opening cart via theme:cart:change event
        // Just set button to success state
        this.setButtonState(button, 'success');

      } catch (error) {
        console.error('Bundle add to cart failed:', error);
        this.setButtonState(button, 'error');
        this.showError(error.message);

      } finally {
        window.RUXTheme.cartProcessing = false;
        setTimeout(() => {
          if (button) {
            this.setButtonState(button, 'default');
          }
        }, 3000);
      }
    }

    collectBundleItems(event) {
      const items = [];
      let variantsInput;

      // Find the form containing the clicked button to avoid collecting duplicates
      const button = event?.target?.closest('[data-add-bundle]');
      const currentForm = button?.closest('form[data-product-form]') ||
                          button?.closest('form') ||
                          button?.closest('.product-single') ||
                          button?.closest('[data-section-type="product"]');

      // Also find the section container for scoping variant selectors
      const currentSection = button?.closest('[data-section-type="product"]') ||
                            button?.closest('.product-single') ||
                            currentForm;

      if (currentForm) {
        RUXUtils.log('Collecting items from current form only');
        variantsInput = currentForm.querySelectorAll('[data-bundles-config]');
      } else {
        RUXUtils.log('No product form found, collecting from document');
        variantsInput = RUXUtils.safeQueryAll('[data-bundles-config]');
      }

      // Check if this is the CFR bundle
      const productHandle = currentForm?.getAttribute('data-product-handle');
      const isCFRBundle = productHandle === 'rux-cfr-snowmobile-cargo-bundle';

      RUXUtils.log(`Product handle: ${productHandle}, isCFRBundle: ${isCFRBundle}`);

      const seenIds = new Set();

      variantsInput.forEach((variant, index) => {
        try {
          const productHandle = variant.getAttribute('data-product-handle');
          RUXUtils.log(`Processing bundle item ${index}, product: ${productHandle}, isCFRBundle: ${isCFRBundle}`);

          // Special handling for CFR bundle brackets - capture all bracket selections
          const isBracketProduct = productHandle === 'cfr-link-it-up-xl-rack-ski-board-brackets';
          if (isBracketProduct) {
            RUXUtils.log('CFR Bundle bracket product detected - looking for bracket selections');

            // Find all selector wrappers for the bracket product within the current section
            const bracketWrappers = currentSection?.querySelectorAll('.selector-wrapper[data-product-handle="cfr-link-it-up-xl-rack-ski-board-brackets"]');
            RUXUtils.log(`Found ${bracketWrappers?.length || 0} bracket selector wrappers`);

            if (bracketWrappers && bracketWrappers.length > 0) {
              bracketWrappers.forEach((wrapper, wrapperIndex) => {
                const checkedInput = wrapper.querySelector('input[data-swatch-input]:checked');

                if (checkedInput) {
                  const bracketValue = checkedInput.value;
                  const bracketVariantId = checkedInput.getAttribute('data-variant-id');

                  RUXUtils.log(`Bracket wrapper ${wrapperIndex}: value="${bracketValue}", variantId=${bracketVariantId}`);

                  // Add to cart if not "No Bracket"
                  if (bracketValue.toLowerCase() !== 'no bracket' && bracketVariantId) {
                    // Check if this variant is already in the items array
                    const existingItem = items.find(item => item.id === bracketVariantId);

                    if (existingItem) {
                      // Same bracket selected on both sides - increment quantity
                      existingItem.quantity += 1;
                      RUXUtils.log(`Bracket ${wrapperIndex + 1}: Incremented quantity for ${bracketValue} (ID: ${bracketVariantId})`);
                    } else {
                      // New bracket - add to items
                      items.push({
                        id: bracketVariantId,
                        quantity: 1
                      });
                      seenIds.add(bracketVariantId); // Add to seenIds to prevent duplicates
                      RUXUtils.log(`Added bracket ${wrapperIndex + 1}: ${bracketValue} (ID: ${bracketVariantId})`);
                    }
                  } else if (bracketValue.toLowerCase() === 'no bracket') {
                    RUXUtils.log(`Bracket wrapper ${wrapperIndex}: Skipping "No Bracket"`);
                  }
                } else {
                  RUXUtils.log(`Bracket wrapper ${wrapperIndex}: No checked input found`);
                }
              });
            }

            // Skip the default variant ID processing for bracket product
            return;
          }

          // Normal processing for all other bundle products
          const variantIdInput = variant.querySelector('[data-bundle-variant-id]');
          const variantQtyInput = variant.querySelector('[data-bundle-variant-qty]');

          const variantId = variantIdInput?.value;
          const variantQty = variantQtyInput?.value;

          RUXUtils.log(`Bundle item ${index} (${productHandle}): variantId from hidden input = ${variantId}`);

          if (variantId && variantQty && !isNaN(parseInt(variantQty))) {
            const qty = parseInt(variantQty);
            if (qty <= 0) return;

            // If same variant selected in another slot (e.g. triple RUX with duplicate
            // color/lid picks), merge into quantity rather than dropping the selection.
            const existingItem = items.find(i => i.id === variantId);
            if (existingItem) {
              existingItem.quantity += qty;
              RUXUtils.log(`Merged bundle item ${index} into existing variant ${variantId}, qty now ${existingItem.quantity}`);
            } else {
              const item = { id: variantId, quantity: qty };
              items.push(item);
              seenIds.add(variantId);
              RUXUtils.log(`Added bundle item ${index}:`, item);
            }
          }
        } catch (error) {
          console.error(`Error processing bundle variant ${index}:`, error, variant);
        }
      });

      RUXUtils.log('Collected bundle items:', items);
      // Log each item clearly
      items.forEach((item, idx) => {
        RUXUtils.log(`  Item ${idx}: ID=${item.id}, Qty=${item.quantity}`);
      });
      return items;
    }

    async addToCart(items) {
      if (!Array.isArray(items) || items.length === 0) {
        throw new Error('No valid items provided to add to cart');
      }

      const validItems = items.filter(item => {
        if (!item || typeof item !== 'object') {
          console.warn('Invalid item object:', item);
          return false;
        }

        if (!item.id || (!item.quantity && item.quantity !== 0)) {
          console.warn('Item missing required properties:', item);
          return false;
        }

        if (item.quantity <= 0) {
          console.warn('Item has invalid quantity:', item);
          return false;
        }

        return true;
      });

      if (validItems.length === 0) {
        throw new Error('No valid items found after validation');
      }

      RUXUtils.log('Sending items to cart:', validItems);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: validItems })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to add to cart (${response.status})`);
      }

      const addResponse = await response.json();

      // Fetch the complete cart state from /cart.js to get full cart object with item_count
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const fullCartData = await cartResponse.json();

      RUXUtils.log('Fetched full cart state:', fullCartData);

      // Dispatch theme:cart:change with complete cart data
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        detail: { cart: fullCartData },
        bubbles: true
      }));

      RUXUtils.log('Dispatched theme:cart:change event with full cart data');

      // Trigger cart reload which refreshes the HTML
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {
          bubbles: true
        }));
        RUXUtils.log('Dispatched theme:cart:reload event');

        // Then open the drawer - dispatch on drawer element (theme listens there)
        setTimeout(() => {
          const cartDrawer = document.querySelector('[data-drawer="drawer-cart"]');
          if (cartDrawer) {
            cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', {
              bubbles: true
            }));
            RUXUtils.log('Opened cart drawer via theme:drawer:open event');
          }
        }, 150);
      }, 50);

      return fullCartData;
    }

    // ------------------------------------------------------------------------
    // CART UI MANAGEMENT
    // Note: Cart refresh and drawer opening is now handled by theme events
    // ------------------------------------------------------------------------

    async fetchCurrentCartData() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.error('Failed to fetch current cart data:', error);
      }
      return null;
    }

    async refreshCartContents() {
      RUXUtils.log('Refreshing cart contents...');

      if (await this.tryThemeRefresh()) {
        RUXUtils.log('Used theme refresh method');
        return;
      }

      if (await this.tryManualRefresh()) {
        RUXUtils.log('Used manual refresh method');
        return;
      }

      await this.trySectionRefresh();
      RUXUtils.log('Used section refresh fallback');
    }

    async tryThemeRefresh() {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        if (!cartResponse.ok) return false;

        const cartData = await cartResponse.json();

        if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
          RUXUtils.log('Using theme drawer renderContents method');
          this.cartDrawer.renderContents(cartData);
          return true;
        }

        if (this.cartNotification && typeof this.cartNotification.renderContents === 'function') {
          RUXUtils.log('Using theme notification renderContents method');
          this.cartNotification.renderContents(cartData);
          return true;
        }

        RUXUtils.log('Theme refresh not available, trying next method');
        return false;
      } catch (error) {
        console.error('Theme refresh failed:', error);
        return false;
      }
    }

    async tryManualRefresh() {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        if (!cartResponse.ok) return false;

        const cartData = await cartResponse.json();
        this.updateCartCount(cartData.item_count);

        if (this.cartDrawer) {
          const cartItemsContainer = this.cartDrawer.querySelector('.drawer__contents, .cart-drawer__contents, [data-cart-items]');
          const cartCountElements = this.cartDrawer.querySelectorAll('[data-cart-count]');

          cartCountElements.forEach(element => {
            element.textContent = cartData.item_count;
          });

          if (cartData.item_count === 0 && cartItemsContainer) {
            cartItemsContainer.innerHTML = '<p>Your cart is empty</p>';
            return true;
          }

          // Return false so we fall through to section refresh which properly updates HTML
          return false;
        }

        return false;
      } catch (error) {
        console.error('Manual refresh failed:', error);
        return false;
      }
    }

    async trySectionRefresh() {
      try {
        const cartSection = this.cartDrawer?.closest('.shopify-section');
        const sectionId = cartSection?.id;

        let url = window.Shopify.routes.root + '?section_id=cart-drawer';

        if (sectionId) {
          const match = sectionId.match(/shopify-section-(.+)/);
          if (match) {
            url = window.Shopify.routes.root + `?section_id=${match[1]}`;
          }
        }

        RUXUtils.log('Fetching cart section from:', url);

        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error('Section fetch failed');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newCartContent = doc.querySelector('#drawer-cart, cart-drawer, .drawer__contents');

        if (newCartContent && this.cartDrawer) {
          const currentContent = this.cartDrawer.querySelector('.drawer__contents, .cart-drawer__contents') || this.cartDrawer;
          const newInnerContent = newCartContent.querySelector('.drawer__contents, .cart-drawer__contents') || newCartContent;

          if (currentContent && newInnerContent) {
            currentContent.innerHTML = newInnerContent.innerHTML;
          } else {
            this.cartDrawer.innerHTML = newCartContent.innerHTML;
          }

          // Clear any loading states
          this.clearDrawerLoadingState();
        }

      } catch (error) {
        console.error('Section refresh failed:', error);
        // Clear loading state even on error
        this.clearDrawerLoadingState();
      }
    }

    clearDrawerLoadingState() {
      if (!this.cartDrawer) return;

      // Remove common loading classes
      this.cartDrawer.classList.remove('is-loading', 'loading', 'drawer--loading');

      // Find and remove loading overlays/spinners
      const loadingOverlay = this.cartDrawer.querySelector('.loading-overlay, .drawer__loading, [data-loading]');
      if (loadingOverlay) {
        loadingOverlay.remove();
        RUXUtils.log('Removed loading overlay from drawer');
      }

      // Re-enable close button
      const closeButton = this.cartDrawer.querySelector('[data-drawer-close], .drawer__close, .cart-drawer__close');
      if (closeButton) {
        closeButton.removeAttribute('disabled');
        closeButton.style.pointerEvents = 'auto';
        RUXUtils.log('Re-enabled close button');
      }

      // Dispatch event that cart is ready
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true
      }));

      RUXUtils.log('Cleared drawer loading state');
    }

    openCartDrawer() {
      RUXUtils.log('Opening cart drawer...');

      if (!this.cartDrawer) {
        RUXUtils.log('No cart drawer found');
        return false;
      }

      const cartToggle = RUXUtils.safeQuery('[data-drawer-toggle="drawer-cart"]');
      if (cartToggle && this.cartDrawer.classList.contains('display-none')) {
        RUXUtils.log('Using cart toggle click method');
        cartToggle.click();
        return true;
      }

      try {
        this.cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', {
          bubbles: false
        }));

        setTimeout(() => {
          if (this.cartDrawer.classList.contains('display-none')) {
            RUXUtils.log('Theme method failed, using force method');
            this.forceOpenDrawer();
          }
        }, 100);

        return true;
      } catch (error) {
        RUXUtils.log('Theme method failed:', error);
      }

      this.forceOpenDrawer();
      return true;
    }

    openDrawerViaToggle() {
      const drawer = RUXUtils.safeQuery('#drawer-cart');
      const toggle = RUXUtils.safeQuery('[data-drawer-toggle="drawer-cart"]');

      if (!drawer || !toggle) {
        RUXUtils.log('Drawer or toggle not found');
        return false;
      }

      if (!drawer.classList.contains('display-none') && drawer.classList.contains('drawer--visible')) {
        RUXUtils.log('Drawer already open');
        return true;
      }

      RUXUtils.log('Opening drawer via toggle button');
      toggle.click();

      setTimeout(() => {
        if (drawer.classList.contains('drawer--visible')) {
          RUXUtils.log('Drawer opened successfully');
        } else {
          RUXUtils.log('Drawer failed to open');
        }
      }, 200);

      return true;
    }

    forceOpenDrawer() {
      RUXUtils.log('Force opening cart drawer');

      if (!this.cartDrawer) return;

      this.cartDrawer.classList.remove('display-none');
      this.cartDrawer.classList.add('drawer--visible');

      const drawerScrolls = this.cartDrawer.querySelector('[data-drawer-scrolls]');
      if (drawerScrolls) {
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {
          bubbles: true,
          detail: drawerScrolls
        }));
      }

      const buttons = RUXUtils.safeQueryAll('[data-drawer-toggle="drawer-cart"]');
      buttons.forEach(btn => btn.setAttribute('aria-expanded', 'true'));

      if (window.theme && window.theme.state) {
        window.theme.state.cartOpen = true;
      }

      this.cartDrawer.style.pointerEvents = 'auto';
      this.cartDrawer.removeAttribute('inert');

      RUXUtils.log('Cart drawer force-opened with classes:', this.cartDrawer.className);
    }

    updateCartCount(count) {
      const cartCountElements = RUXUtils.safeQueryAll('.cart-count, [data-cart-count], .cart__count');
      cartCountElements.forEach(element => {
        element.textContent = count;

        if (count > 0) {
          element.classList.remove('hidden', 'hide');
          element.style.display = '';
        } else {
          element.classList.add('hidden');
        }
      });
    }

    // ------------------------------------------------------------------------
    // BUTTON STATE MANAGEMENT
    // ------------------------------------------------------------------------

    setButtonState(button, state) {
      if (!button) return;

      const buttonText = button.querySelector("[data-bundle-button-text], [data-add-to-cart-text]") || button;

      switch (state) {
        case 'loading':
          button.setAttribute("disabled", "disabled");
          button.classList.add('loading');
          this.updateButtonText(buttonText, "Adding...");
          break;

        case 'success':
          button.removeAttribute("disabled");
          button.classList.remove('loading');
          button.classList.add('success');
          this.updateButtonText(buttonText, "Added!");
          break;

        case 'error':
          button.removeAttribute("disabled");
          button.classList.remove('loading');
          button.classList.add('error');
          this.updateButtonText(buttonText, "Try Again");
          break;

        case 'default':
          button.removeAttribute("disabled");
          button.classList.remove('loading', 'success', 'error');
          this.updateButtonText(buttonText, "Add to Cart");
          break;
      }
    }

    updateButtonText(element, text) {
      if (element && element !== element.closest('button')) {
        element.textContent = text;
      } else if (element) {
        element.textContent = text;
      }
    }

    // ------------------------------------------------------------------------
    // ERROR HANDLING
    // ------------------------------------------------------------------------

    showError(message) {
      console.error('Cart Error:', message);

      const notificationContainer = RUXUtils.safeQuery('.notification, .alert, .error-message');

      if (notificationContainer) {
        notificationContainer.textContent = message;
        notificationContainer.style.display = 'block';

        setTimeout(() => {
          notificationContainer.style.display = 'none';
        }, 5000);
      } else if (window.RUXTheme?.debug) {
        alert(`Cart Error: ${message}`);
      }

      document.dispatchEvent(new CustomEvent('rux:cart:error', {
        detail: { message },
        bubbles: true
      }));
    }

    // ------------------------------------------------------------------------
    // CLEANUP
    // ------------------------------------------------------------------------

    destroy() {
      this.eventListeners.forEach((handler, name) => {
        try {
          // Remove with capture:true to match how they were added
          document.removeEventListener('click', handler, { capture: true });
          document.removeEventListener('submit', handler, { capture: true });
          document.removeEventListener('theme:cart:change', handler);
          RUXUtils.log(`Removed event listener: ${name}`);
        } catch (error) {
          console.error(`Error removing event listener ${name}:`, error);
        }
      });

      this.eventListeners.clear();
      window.RUXTheme.cartProcessing = false;
      RUXCartSystem.instance = null;
    }
  }

  // ============================================================================
  // BUNDLE HANDLER - Simplified and organized
  // ============================================================================

  class RUXBundleHandler {
    constructor() {
      this.isInitialized = false;
      this.lastRun = 0;
      this.throttledRun = RUXUtils.throttle(this.runBundleCalculation.bind(this), 300);
      this.bundleForm = null; // Reference to the bundle form for scoped queries

      this.init();
    }

    // ------------------------------------------------------------------------
    // INITIALIZATION
    // ------------------------------------------------------------------------

    init() {
      if (this.isInitialized || !this.hasBundles()) {
        return;
      }

      this.isInitialized = true;

      // Find and store bundle form reference for scoped queries
      this.bundleForm = document.querySelector('form[data-product-handle]');
      if (!this.bundleForm) {
        RUXUtils.log('Warning: Bundle form not found');
      }

      // Detect currency code setting BEFORE any updates
      this.detectCurrencyCodeSetting();

      this.setupUI();
      this.bindEvents();

      // Run calculation immediately on init
      this.runBundleCalculation();

      // Also run after a short delay to ensure DOM is fully ready
      setTimeout(() => {
        this.runBundleCalculation();
      }, 100);

      RUXUtils.log('Bundle Handler initialized');
    }

    detectCurrencyCodeSetting() {
      // Look at original Liquid-rendered prices to detect currency code setting
      const moneyElements = document.querySelectorAll('.money');
      for (const el of moneyElements) {
        const text = el.textContent.trim();
        // Check if it contains currency code like "USD", "EUR", "CAD", etc.
        if (/\b[A-Z]{3}\b/.test(text)) {
          this._showCurrencyCode = true;
          RUXUtils.log('Currency code detected in Liquid prices - will include in formatted prices');
          return;
        }
      }

      this._showCurrencyCode = false;
      RUXUtils.log('No currency code in Liquid prices - will omit from formatted prices');
    }

    hasBundles() {
      return !!RUXUtils.safeQuery("[data-bundle-variant-id]");
    }

    // ------------------------------------------------------------------------
    // UI SETUP
    // ------------------------------------------------------------------------

    setupUI() {
      RUXUtils.safeQueryAll("[data-bundle-price]").forEach(element => {
        element.classList.add("product__price--sale");
      });

      RUXUtils.safeQueryAll("[data-price-off]").forEach(element => {
        element.classList.remove("hide");
      });

      // Initialize variant displays with current selections
      this.initializeVariantDisplays();
    }

    initializeVariantDisplays() {
      // Find all checked variant inputs and update their displays
      const checkedInputs = RUXUtils.safeQueryAll('input[name^="options["]:checked');
      checkedInputs.forEach(input => {
        this.updateVariantDisplay(input);
      });
    }

    // ------------------------------------------------------------------------
    // EVENT HANDLING
    // ------------------------------------------------------------------------

    bindEvents() {
      document.addEventListener("theme:variant:change", this.throttledRun);
      document.addEventListener('change', this.handleVariantChange.bind(this));

      // Also trigger on input events for more responsive updates
      document.addEventListener('input', this.handleVariantChange.bind(this));

      // Trigger on custom bundle variant change events
      document.addEventListener('rux:bundle:variant:change', this.throttledRun);
    }

    handleVariantChange(event) {
      const input = event.target;
      if ((input.hasAttribute('data-swatch-input') || input.name?.includes('options[')) && input.closest('.product__block__variants')) {
        RUXUtils.log('Bundle variant changed:', input.value);

        // Update the data-swapper-target display for this variant option
        this.updateVariantDisplay(input);

        this.throttledRun();

        // Dispatch custom event for other handlers
        document.dispatchEvent(new CustomEvent('rux:bundle:variant:change', {
          detail: { input },
          bubbles: true
        }));
      }
    }

    updateVariantDisplay(input) {
      // Find the closest selector wrapper containing this input
      const selectorWrapper = input.closest('.selector-wrapper');
      if (!selectorWrapper) return;

      // Find the data-swapper-target display element within this selector
      const displayElement = selectorWrapper.querySelector('[data-swapper-target]');
      if (displayElement && input.checked) {
        displayElement.textContent = input.value;
        RUXUtils.log(`Updated variant display to: ${input.value}`);
      }
    }

    // ------------------------------------------------------------------------
    // BUNDLE CALCULATIONS
    // ------------------------------------------------------------------------

    runBundleCalculation() {
      try {
        RUXUtils.log('Running bundle calculation...');
        const bundleData = this.calculateBundlePrice();
        if (bundleData) {
          RUXUtils.log('Bundle calculation successful:', bundleData);
          this.updateUI(bundleData);
        } else {
          console.warn('Bundle calculation returned null - check if data-bundle-discount exists');
        }
      } catch (error) {
        console.error('Bundle calculation error:', error);
        console.error('Error stack:', error.stack);
      }
    }

    calculateBundlePrice() {
      const bundleDiscountElement = RUXUtils.safeQuery("[data-bundle-discount]");
      if (!bundleDiscountElement) {
        console.warn('Bundle calculation failed: [data-bundle-discount] element not found');
        return null;
      }

      const bundleDiscountAttr = bundleDiscountElement.getAttribute("data-bundle-discount");
      RUXUtils.log('Bundle discount attribute value:', bundleDiscountAttr);

      const bundleDiscount = parseInt(bundleDiscountAttr || "0");
      if (isNaN(bundleDiscount)) {
        console.warn('Bundle calculation failed: bundle discount is not a number. Value:', bundleDiscountAttr);
        return null;
      }

      if (bundleDiscount < 0 || bundleDiscount > 100) {
        console.warn('Bundle discount is out of range (0-100):', bundleDiscount);
      }

      const bundleDiscountPercentage = bundleDiscount / 100;
      let variantPrice = 0;
      let soldOut = false;
      const variantIds = [];
      // Key by slot position (1-indexed, matching Liquid forloop.index and the
      // hidden input `name="id-N"`). Keying by product handle collapses triple
      // bundles where multiple slots share the same product handle.
      const variantIdsBySlot = new Map();
      let totalQty = 0;

      // Get currency info to properly parse prices
      const currency = this.getCurrencyInfo();
      RUXUtils.log('Currency info:', currency);

      // Get compare-at price for accurate savings calculation
      const compareAtPriceStr = bundleDiscountElement.getAttribute("data-bundle-price") || "0";
      let compareAtPrice = this.parsePrice(compareAtPriceStr, currency.usesCommaDecimal);

      const variantBlocks = RUXUtils.safeQueryAll(".product__block__variants");
      RUXUtils.log(`Found ${variantBlocks.length} variant blocks`);

      variantBlocks.forEach((block, index) => {
        // Check if this block contains bracket product options
        const bracketWrappers = block.querySelectorAll('.selector-wrapper[data-product-handle="cfr-link-it-up-xl-rack-ski-board-brackets"]');

        if (bracketWrappers && bracketWrappers.length > 0) {
          RUXUtils.log(`Block ${index}: Found ${bracketWrappers.length} bracket selector wrappers`);

          // Handle bracket product - count all checked bracket inputs
          // Note: Bracket product has no hidden inputs, so we DON'T add to variantIdsBySlot map
          bracketWrappers.forEach((wrapper, wrapperIndex) => {
            const checkedInput = wrapper.querySelector("[data-swatch-input]:checked");
            if (!checkedInput) {
              RUXUtils.log(`Block ${index}, Bracket wrapper ${wrapperIndex}: No checked input`);
              return;
            }

            const bracketValue = checkedInput.value;
            if (bracketValue.toLowerCase() === 'no bracket') {
              RUXUtils.log(`Block ${index}, Bracket wrapper ${wrapperIndex}: Skipping "No Bracket"`);
              return;
            }

            const variantId = checkedInput.getAttribute("data-variant-id");
            const priceStr = checkedInput.getAttribute("data-variant-price") || "0";
            const price = this.parsePrice(priceStr, currency.usesCommaDecimal);

            RUXUtils.log(`Block ${index}, Bracket wrapper ${wrapperIndex}: ${bracketValue}, price=${priceStr}, parsed=${price}`);

            variantIds.push(variantId);
            // DON'T add to variantIdsBySlot - brackets have no hidden inputs

            if (checkedInput.classList.contains("sold-out")) {
              soldOut = true;
            }

            if (!isNaN(price)) {
              variantPrice += price;
              totalQty += 1;
            } else {
              console.warn(`Block ${index}, Bracket wrapper ${wrapperIndex}: Failed to parse price "${priceStr}"`);
            }
          });

          return; // Skip normal processing for bracket product
        }

        // Normal processing for all other products
        const checkedInput = block.querySelector("[data-swatch-input]:checked");
        if (!checkedInput) {
          RUXUtils.log(`Block ${index}: No checked input found`);
          return;
        }

        const variantId = checkedInput.getAttribute("data-variant-id");
        const priceStr = checkedInput.getAttribute("data-variant-price") || "0";
        const qty = parseInt(checkedInput.getAttribute("data-variant-quantity") || "1");

        // Get product handle from selector wrapper
        const selectorWrapper = checkedInput.closest('.selector-wrapper[data-product-handle]');
        const productHandle = selectorWrapper?.getAttribute('data-product-handle');

        RUXUtils.log(`Block ${index}: product=${productHandle}, variant ID=${variantId}, price=${priceStr}, qty=${qty}`);

        variantIds.push(variantId);

        // Store variant ID by slot position (1-indexed) so slots sharing a
        // product handle (triple bundle) don't collapse to a single entry.
        const slotPosition = index + 1;
        variantIdsBySlot.set(slotPosition, variantId);

        if (checkedInput.classList.contains("sold-out")) {
          soldOut = true;
        }

        const price = this.parsePrice(priceStr, currency.usesCommaDecimal);

        if (!isNaN(price)) {
          variantPrice += price * qty;
          totalQty += qty;
        } else {
          console.warn(`Block ${index}: Failed to parse price "${priceStr}"`);
        }
      });

      const discountAmount = variantPrice * bundleDiscountPercentage;
      const finalPrice = Math.floor(variantPrice - discountAmount);

      // Calculate savings
      // compareAtPrice from data-bundle-price already represents the total for all products
      let savings;
      if (compareAtPrice > finalPrice) {
        savings = Math.floor(compareAtPrice - finalPrice);
      } else {
        savings = Math.floor(discountAmount);
      }

      return {
        finalPrice,
        savings,
        variantIds,
        variantIdsBySlot, // Map of slot position (1-indexed) to variant IDs for updating hidden inputs
        soldOut,
        currency
      };
    }

    getCurrencyInfo() {
      const currencyCode = theme?.currencyCode || 'USD';
      const symbols = {
        'USD': '$',
        'CAD': '$',
        'EUR': '€',
        'GBP': '£',
        'CHF': 'CHF ',
        'SEK': 'kr ',
        'DKK': 'kr ',
        'NOK': 'kr '
      };

      return {
        code: currencyCode,
        symbol: symbols[currencyCode] || '$',
        usesCommaDecimal: ['EUR', 'NOK', 'DKK'].includes(currencyCode)
      };
    }

    parsePrice(priceStr, usesCommaDecimal) {
      if (!priceStr) return 0;

      RUXUtils.log(`Parsing price: "${priceStr}", usesCommaDecimal: ${usesCommaDecimal}`);

      // For currencies that use comma as decimal separator (EUR, NOK, DKK)
      // Example: "1.234,56" -> remove periods, replace comma with period -> "1234.56"
      if (usesCommaDecimal) {
        const cleaned = priceStr.replace(/\./g, '').replace(',', '.');
        const result = parseFloat(cleaned.replace(/[^\d.]/g, ''));
        RUXUtils.log(`  → Comma-decimal parse: "${priceStr}" → ${result}`);
        return result;
      }

      // For currencies that use period as decimal separator (USD, CAD)
      // Example: "1,234.56" -> remove commas -> "1234.56"
      const cleaned = priceStr.replace(/,/g, '');
      const result = parseFloat(cleaned.replace(/[^\d.]/g, ''));
      RUXUtils.log(`  → Period-decimal parse: "${priceStr}" → ${result}`);
      return result;
    }

    formatPrice(amount, currency) {
      // Round to 2 decimal places
      const rounded = Math.round(amount * 100) / 100;

      // Check if currency code should be shown by looking at existing price elements
      // This ensures we match the Shopify theme setting
      const showCurrencyCode = this.shouldShowCurrencyCode();

      // For currencies using comma as decimal separator
      if (currency.usesCommaDecimal) {
        // Format: 1.234,56 or 1.234,56 EUR
        const parts = rounded.toFixed(2).split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const formatted = `${integerPart},${parts[1]}`;
        return showCurrencyCode
          ? `${currency.symbol}${formatted} ${currency.code}`
          : `${currency.symbol}${formatted}`;
      }

      // For currencies using period as decimal separator
      // Format: $1,234.56 or $1,234.56 USD
      const parts = rounded.toFixed(2).split('.');
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const formatted = `${currency.symbol}${integerPart}.${parts[1]}`;
      return showCurrencyCode ? `${formatted} ${currency.code}` : formatted;
    }

    shouldShowCurrencyCode() {
      // Use the setting detected during initialization
      return this._showCurrencyCode || false;
    }

    // ------------------------------------------------------------------------
    // AVAILABILITY CALCULATION
    // ------------------------------------------------------------------------

    calculateBundleAvailability() {
      let available = true;
      let minInventory = Infinity;
      let hasAnyTracking = false;
      const variantBlocks = RUXUtils.safeQueryAll(".product__block__variants");

      RUXUtils.log('Calculating bundle availability...');

      variantBlocks.forEach((block, index) => {
        const checkedInput = block.querySelector("[data-swatch-input]:checked");
        if (!checkedInput) {
          RUXUtils.log(`Availability Block ${index}: No checked input`);
          return;
        }

        const variantId = checkedInput.getAttribute("data-variant-id");

        // Find the product JSON for this bundle product
        const productJsonElement = block.closest('[data-product-form-outer]')?.querySelector('[data-product-json]') ||
                                   document.querySelector(`[data-product-json]`);

        if (!productJsonElement) {
          RUXUtils.log(`Availability Block ${index}: No product JSON found`);
          return;
        }

        let productData;
        try {
          productData = JSON.parse(productJsonElement.textContent);
        } catch (e) {
          console.warn(`Failed to parse product JSON for block ${index}:`, e);
          return;
        }

        // Handle both single product and array of products (bundles)
        const products = Array.isArray(productData) ? productData : [productData];
        let variant = null;

        // Find the variant in the product(s)
        for (const product of products) {
          variant = product.variants?.find(v => v.id == variantId);
          if (variant) break;
        }

        if (!variant) {
          RUXUtils.log(`Availability Block ${index}: Variant ${variantId} not found in JSON`);
          available = false;
          return;
        }

        RUXUtils.log(`Availability Block ${index}: variant=${variantId}, available=${variant.available}, inventory=${variant.inventory_quantity}`);

        // Check Shopify availability flag
        if (!variant.available) {
          available = false;
          RUXUtils.log(`  → Variant marked unavailable`);
        }

        // Check inventory if tracked
        if (variant.inventory_management) {
          hasAnyTracking = true;

          if (variant.inventory_policy === 'deny') {
            // Can't oversell - check quantity
            const qty = variant.inventory_quantity || 0;

            if (qty <= 0) {
              available = false;
              RUXUtils.log(`  → Out of stock (qty=${qty})`);
            }

            minInventory = Math.min(minInventory, qty);
          } else {
            // Can oversell (inventory_policy === 'continue')
            RUXUtils.log(`  → Overselling allowed`);
          }
        } else {
          RUXUtils.log(`  → No inventory tracking`);
        }
      });

      // If nothing is tracked, set minInventory to null
      if (!hasAnyTracking || minInventory === Infinity) {
        minInventory = null;
      }

      const result = {
        available,
        minInventory,
        hasTracking: hasAnyTracking
      };

      RUXUtils.log('Bundle availability result:', result);
      return result;
    }

    updateAvailabilityDisplay(availabilityData) {
      const remainingWrapper = RUXUtils.safeQuery('[data-remaining-wrapper]');
      if (!remainingWrapper) {
        RUXUtils.log('No [data-remaining-wrapper] element found - availability display not enabled');
        return;
      }

      const remainingCount = remainingWrapper.querySelector('[data-remaining-count]');
      const maxThreshold = parseInt(remainingWrapper.querySelector('[data-remaining-max]')?.getAttribute('data-remaining-max')) || 20;

      RUXUtils.log('Updating availability display:', availabilityData);

      // Remove all existing state classes
      remainingWrapper.classList.remove('count-is-in', 'count-is-low', 'count-is-out', 'count-is-unavailable');

      const { available, minInventory, hasTracking } = availabilityData;

      // Determine which class to apply
      if (!available || !hasTracking) {
        // Not available or no tracking
        if (!available && minInventory !== null && minInventory === 0) {
          remainingWrapper.classList.add('count-is-out');
          RUXUtils.log('  → State: Out of Stock');
        } else if (!hasTracking) {
          remainingWrapper.classList.add('count-is-in');
          RUXUtils.log('  → State: In Stock (no tracking)');
        } else {
          remainingWrapper.classList.add('count-is-unavailable');
          RUXUtils.log('  → State: Unavailable');
        }
      } else if (minInventory !== null) {
        // Has tracking and available
        if (minInventory <= 0) {
          remainingWrapper.classList.add('count-is-out');
          RUXUtils.log('  → State: Out of Stock (0)');
        } else if (minInventory <= maxThreshold) {
          remainingWrapper.classList.add('count-is-low');
          RUXUtils.log(`  → State: Low Stock (${minInventory} ≤ ${maxThreshold})`);
        } else {
          remainingWrapper.classList.add('count-is-in');
          RUXUtils.log(`  → State: In Stock (${minInventory} > ${maxThreshold})`);
        }

        // Update the count display
        if (remainingCount) {
          remainingCount.textContent = minInventory;
        }
      } else {
        // Available but no inventory numbers
        remainingWrapper.classList.add('count-is-in');
        RUXUtils.log('  → State: In Stock (available, no quantity)');
      }
    }

    // ------------------------------------------------------------------------
    // UI UPDATES
    // ------------------------------------------------------------------------

    updateUI(bundleData) {
      const { finalPrice, savings, variantIds, variantIdsBySlot, soldOut, currency } = bundleData;

      this.updateAddToCartButton(soldOut);

      const formattedPrice = this.formatPrice(finalPrice, currency);
      const formattedSavings = this.formatPrice(savings, currency);

      this.updatePriceDisplays(formattedPrice);
      this.updateSavingsDisplays(formattedSavings);
      this.updateVariantIds(variantIdsBySlot); // Slot-keyed map (1-indexed)

      // Update availability display
      const availabilityData = this.calculateBundleAvailability();
      this.updateAvailabilityDisplay(availabilityData);
    }

    updateAddToCartButton(soldOut) {
      const submitButton = RUXUtils.safeQuery("[data-add-bundle]");
      if (!submitButton) return;

      const buttonText = submitButton.querySelector("[data-bundle-button-text]");

      if (soldOut) {
        submitButton.setAttribute("disabled", "disabled");
        if (buttonText) buttonText.textContent = "Sold Out";
      } else {
        submitButton.removeAttribute("disabled");
        if (buttonText) buttonText.textContent = "Add to Cart";
      }
    }

    updatePriceDisplays(formattedPrice) {
      RUXUtils.log('Updating price displays with:', formattedPrice);

      // Track which .money elements we've updated to avoid duplicates
      const updatedElements = new Set();

      // Helper function to update a price container
      const updatePriceContainer = (container, selector) => {
        const moneyElement = container.querySelector(".money");
        if (moneyElement && !updatedElements.has(moneyElement)) {
          moneyElement.textContent = formattedPrice;
          updatedElements.add(moneyElement);
          RUXUtils.log(`Updated ${selector}:`, moneyElement.textContent);
        } else if (!moneyElement) {
          const newMoneyElement = this.createMoneyElement();
          newMoneyElement.textContent = formattedPrice;
          container.appendChild(newMoneyElement);
          updatedElements.add(newMoneyElement);
          RUXUtils.log(`Created and added ${selector}:`, newMoneyElement.textContent);
        }
      };

      // Update bundle prices first (most specific)
      RUXUtils.safeQueryAll("[data-bundle-price]").forEach(container => {
        updatePriceContainer(container, '[data-bundle-price]');
      });

      // Update button prices (bundles use data-button-price)
      RUXUtils.safeQueryAll("[data-button-price]").forEach(container => {
        updatePriceContainer(container, '[data-button-price]');
      });

      // Update regular product prices
      RUXUtils.safeQueryAll("[data-product-price]").forEach(container => {
        updatePriceContainer(container, '[data-product-price]');
      });

      // Only update .money elements that are direct children of the main product section
      // Skip updating individual product prices within variant selectors
      const mainProductSection = document.querySelector('.product-section, [data-section-type="product"]');
      if (mainProductSection) {
        const standalonePrices = mainProductSection.querySelectorAll('.money');
        standalonePrices.forEach(element => {
          // Skip if already updated, excluded, or inside a variant selector
          if (updatedElements.has(element) ||
              this.isExcludedPriceElement(element) ||
              element.closest('.product__block__variants')) {
            return;
          }

          element.textContent = formattedPrice;
          updatedElements.add(element);
          RUXUtils.log('Updated standalone .money:', element.textContent);
        });
      }

      RUXUtils.log(`Total elements updated: ${updatedElements.size}`);
    }

    updateSavingsDisplays(formattedSavings) {
      RUXUtils.safeQueryAll("[data-price-off-amount]").forEach(element => {
        element.textContent = formattedSavings;

        const parent = element.closest("[data-price-off]");
        if (parent) {
          parent.classList.remove("hide");
        }
      });
    }

    updateVariantIds(variantIdsBySlot) {
      // Scope query to the bundle form only to prevent updating wrong inputs
      if (!this.bundleForm) {
        RUXUtils.log('Cannot update variant IDs - bundle form not found');
        return;
      }

      if (!variantIdsBySlot || !(variantIdsBySlot instanceof Map)) {
        RUXUtils.log('Invalid variantIdsBySlot - expected Map');
        return;
      }

      const inputs = this.bundleForm.querySelectorAll("[data-bundle-variant-id]");
      RUXUtils.log(`Updating ${inputs.length} hidden inputs using slot position map`);

      inputs.forEach((input) => {
        // Hidden inputs are named `id-N` where N is the bundle slot position
        // (1-indexed Liquid forloop.index). Match by slot, not product handle,
        // so triple bundles with duplicate product handles update correctly.
        const slotMatch = input.name && input.name.match(/^id-(\d+)$/);
        if (!slotMatch) {
          RUXUtils.log(`Warning: Hidden input has unexpected name "${input.name}"`);
          return;
        }

        const slotPosition = parseInt(slotMatch[1], 10);
        const variantId = variantIdsBySlot.get(slotPosition);

        if (variantId) {
          RUXUtils.log(`Updating hidden input for slot ${slotPosition}: ${variantId}`);
          input.value = variantId;
        } else {
          RUXUtils.log(`Warning: No variant ID found for slot ${slotPosition}`);
        }
      });
    }

    isExcludedPriceElement(element) {
      // Exclude compare prices, preorder prices, and savings amounts
      return element.closest("[data-price-discount]") ||
             element.closest("[data-preorder-price]") ||
             element.closest("[data-price-off-amount]");
    }

    createMoneyElement() {
      const element = document.createElement("span");
      element.className = "money";
      return element;
    }
  }

  // ============================================================================
  // LID HANDLER - Enhanced and organized
  // ============================================================================

  class RUXLidHandler {
    constructor() {
      this.config = window.RUXTheme.config;
      this.pageType = null;
      this.isInitialized = false;
      this.activeContexts = new Map();

      this.init();
    }

    // ------------------------------------------------------------------------
    // INITIALIZATION
    // ------------------------------------------------------------------------

    init() {
      if (this.isInitialized) return;

      RUXUtils.log('Lid Handler: Initializing');

      this.addStyles();
      this.detectPageType();

      if (this.pageType.type !== 'none') {
        this.setupHandlers();
        this.isInitialized = true;
        RUXUtils.log(`Lid Handler: Successfully initialized for ${this.pageType.type}`);
      } else {
        RUXUtils.log('Lid Handler: No RUX products detected, skipping');
      }
    }

    addStyles() {
      if (document.getElementById('rux-lid-handler-styles')) return;

      const styles = `
        /* RUX Lid Handler Styles */
        .radio__button.rux-hidden,
        .radio__button[data-rux-hidden="true"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          height: 0 !important;
          width: 0 !important;
          position: absolute !important;
          z-index: -9999 !important;
        }
        
        .radio__button.rux-hidden input,
        .radio__button[data-rux-hidden="true"] input {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        
        [data-select-label*="Lid"] .radio__button input.unavailable + label::before,
        [data-select-label*="Lid"] .radio__button input.sold-out + label::before,
        .selector-wrapper[data-select-label*="Lid"] .radio__button input.unavailable + label::before,
        .selector-wrapper[data-select-label*="Lid"] .radio__button input.sold-out + label::before {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          content: none !important;
        }
        
        [data-select-label*="Color"] .swatch__button input.unavailable ~ label::before,
        .selector-wrapper[data-select-label*="Color"] .swatch__button input.unavailable ~ label::before {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          content: none !important;
        }
        
        [data-select-label*="Color"] .swatch__button input.unavailable + label,
        .selector-wrapper[data-select-label*="Color"] .swatch__button input.unavailable + label {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        
        [data-select-label*="Lid"] .radio__button:not(.rux-hidden):not([data-rux-hidden="true"]) input + label {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
      `;

      const styleElement = document.createElement('style');
      styleElement.id = 'rux-lid-handler-styles';
      styleElement.textContent = styles;
      document.head.appendChild(styleElement);
    }

    // ------------------------------------------------------------------------
    // PAGE TYPE DETECTION
    // ------------------------------------------------------------------------

    detectPageType() {
      const bundleSelectors = Array.from(RUXUtils.safeQueryAll('input[type="radio"]'))
        .map(input => input.name?.match(/options\[bundle_(\d+)_Lid\]/)?.[1])
        .filter(Boolean)
        .filter((value, index, array) => array.indexOf(value) === index);

      const hasSingleProduct = RUXUtils.safeQueryAll('input[name="options[Color]"]').length > 0 &&
        RUXUtils.safeQueryAll('input[name="options[Lid]"]').length > 0;

      if (bundleSelectors.length > 1) {
        this.pageType = { type: 'triple', bundleIndices: bundleSelectors };
      } else if (bundleSelectors.length === 1) {
        this.pageType = { type: 'bundle', bundleIndex: bundleSelectors[0] };
      } else if (hasSingleProduct) {
        this.pageType = { type: 'single' };
      } else {
        this.pageType = { type: 'none' };
        return;
      }

      RUXUtils.log('Lid Handler: Detected page type:', this.pageType);
    }

    // ------------------------------------------------------------------------
    // HANDLER SETUP
    // ------------------------------------------------------------------------

    setupHandlers() {
      switch (this.pageType.type) {
        case 'single':
          this.setupSingleProduct();
          break;
        case 'bundle':
          this.setupBundle(this.pageType.bundleIndex);
          break;
        case 'triple':
          this.setupTripleBundle(this.pageType.bundleIndices);
          break;
      }

      setTimeout(() => this.runInitialSetup(), 200);
    }

    setupSingleProduct() {
      const context = {
        type: 'single',
        bundleIndex: null,
        colorInputs: RUXUtils.safeQueryAll('input[name="options[Color]"]'),
        lidInputs: RUXUtils.safeQueryAll('input[name="options[Lid]"]'),
        lidDisplay: RUXUtils.safeQuery('[data-option-position="2"] [data-swapper-target]')
      };

      this.activeContexts.set('single', context);
      this.bindLidLogic(context);
    }

    setupBundle(bundleIndex) {
      const context = {
        type: 'bundle',
        bundleIndex,
        colorInputs: RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Color]"]`),
        lidInputs: RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Lid]"]`),
        lidDisplay: RUXUtils.safeQuery(`[data-select-label*="bundle_${bundleIndex}"][data-select-label*="Lid"] [data-swapper-target]`)
      };

      this.activeContexts.set(`bundle_${bundleIndex}`, context);
      this.bindLidLogic(context);
    }

    setupTripleBundle(bundleIndices) {
      this.fixDuplicateIds(bundleIndices);

      bundleIndices.forEach(bundleIndex => {
        const context = {
          type: 'triple',
          bundleIndex,
          colorInputs: RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Color]"]`),
          lidInputs: RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Lid]"]`),
          lidDisplay: null
        };

        this.addBundleIdentifiers(bundleIndex);
        context.lidDisplay = this.findLidDisplay(bundleIndex);

        this.activeContexts.set(`bundle_${bundleIndex}`, context);
        this.bindLidLogic(context);
      });
    }

    findLidDisplay(bundleIndex) {
      const selectors = [
        `[data-swapper-target][data-bundle-index="${bundleIndex}"]`,
        `[data-select-label*="bundle_${bundleIndex}"][data-select-label*="Lid"] [data-swapper-target]`,
        `.selector-wrapper:has(input[name*="bundle_${bundleIndex}_Lid"]) [data-swapper-target]`
      ];

      for (const selector of selectors) {
        try {
          const element = RUXUtils.safeQuery(selector);
          if (element) {
            RUXUtils.log(`Found lid display for bundle ${bundleIndex}:`, selector);
            return element;
          }
        } catch (e) {
          continue;
        }
      }

      const lidInputs = RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Lid]"]`);
      for (const input of lidInputs) {
        const container = input.closest('.selector-wrapper');
        const swapperTarget = container?.querySelector('[data-swapper-target]');
        if (swapperTarget) {
          RUXUtils.log(`Found lid display for bundle ${bundleIndex} via traversal`);
          return swapperTarget;
        }
      }

      RUXUtils.log(`Could not find lid display for bundle ${bundleIndex}`);
      return null;
    }

    fixDuplicateIds(bundleIndices) {
      bundleIndices.forEach(bundleIndex => {
        const inputs = RUXUtils.safeQueryAll(`
          input[name="options[bundle_${bundleIndex}_Color]"],
          input[name="options[bundle_${bundleIndex}_Lid]"]
        `);

        inputs.forEach(input => {
          const oldId = input.id;
          if (oldId && !oldId.includes(`-bundle-${bundleIndex}`)) {
            const newId = `${oldId}-bundle-${bundleIndex}`;
            input.id = newId;

            const label = RUXUtils.safeQuery(`label[for="${oldId}"]`);
            if (label) {
              label.setAttribute('for', newId);
            }
          }
        });
      });
    }

    addBundleIdentifiers(bundleIndex) {
      const colorInputs = RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Color]"]`);
      const lidInputs = RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Lid]"]`);

      [...colorInputs, ...lidInputs].forEach(input => {
        input.setAttribute('data-bundle-index', bundleIndex);

        const container = input.closest('.selector-wrapper, .product__block__variants');
        if (container) {
          container.setAttribute('data-bundle-index', bundleIndex);
        }
      });

      const lidContainers = RUXUtils.safeQueryAll(`input[name="options[bundle_${bundleIndex}_Lid]"]`);
      lidContainers.forEach(input => {
        const container = input.closest('.selector-wrapper');
        const swapperTarget = container?.querySelector('[data-swapper-target]');
        if (swapperTarget) {
          swapperTarget.setAttribute('data-bundle-index', bundleIndex);
        }
      });
    }

    // ------------------------------------------------------------------------
    // LID LOGIC BINDING
    // ------------------------------------------------------------------------

    bindLidLogic(context) {
      if (!context.colorInputs.length || !context.lidInputs.length) {
        RUXUtils.log(`Lid Handler (${context.type}): Missing inputs - colors: ${context.colorInputs.length}, lids: ${context.lidInputs.length}`);
        return;
      }

      RUXUtils.log(`Lid Handler (${context.type}${context.bundleIndex ? ` bundle ${context.bundleIndex}` : ''}): Binding logic for ${context.colorInputs.length} color inputs and ${context.lidInputs.length} lid inputs`);

      context.lidInputs.forEach(input => {
        input.setAttribute('data-rux-original-state', 'available');
      });

      const handleColorChange = (event) => {
        const input = event.target;

        if (!Array.from(context.colorInputs).includes(input)) return;

        if (context.bundleIndex) {
          const inputBundleIndex = input.getAttribute('data-bundle-index') ||
            input.name?.match(/bundle_(\d+)_/)?.[1];
          if (inputBundleIndex !== context.bundleIndex) return;
        }

        RUXUtils.log(`Lid Handler (${context.type}${context.bundleIndex ? ` bundle ${context.bundleIndex}` : ''}): Color changed to ${input.value}`);

        setTimeout(() => {
          this.updateLidOptions(context, input.value);
        }, 50);
      };

      // Bind WITHOUT cloning the node. The previous implementation cloned each
      // color input and swapped it in, which silently discarded the theme's
      // native ProductForm `change` listeners (theme.dev.js binds one listener
      // per [name^="options"] input). Preserving those listeners lets the native
      // engine recompute availability, sold-out styling, the Restock "Notify me"
      // button, price and the URL when the variant changes.
      const colorInputsArray = Array.from(context.colorInputs);

      colorInputsArray.forEach((input) => {
        if (input.dataset.ruxColorBound === 'true') return; // avoid double-binding our own handler
        input.dataset.ruxColorBound = 'true';
        input.addEventListener('change', handleColorChange);
        RUXUtils.log(`Bound color change listener for: ${input.value}`);
      });

      context.colorInputs = colorInputsArray;
      RUXUtils.log(`Lid Handler (${context.type}): Successfully bound ${colorInputsArray.length} color input listeners`);
    }

    // ------------------------------------------------------------------------
    // LID OPTIONS UPDATE
    // ------------------------------------------------------------------------

    getProductHandle() {
      // Try to get from a data attribute or class on the product-page container
      const productPage = document.querySelector('.product-page');
      if (productPage) {
        // Example: <div class="product-page rux-70l">
        const classes = productPage.className.split(' ');
        const handle = classes.find(c => c.startsWith('rux-'));
        if (handle) return handle;
      }
      // Fallback: Try from a global variable or meta tag
      const metaHandle = document.querySelector('meta[property="og:product_handle"]');
      if (metaHandle) return metaHandle.content;
      return '';
    }

    // Map a color to its single paired lid. The product only has variants for
    // each color's paired lid (e.g. Black→Tri-fold, Bronze/Mars Red→Press-seal),
    // so this 1:1 mapping is the source of truth for both lid swapping and the
    // per-swatch out-of-stock marks.
    getPairedLidForColor(color) {
      if (color === this.config.marsRedColor || color === this.config.bronzeColor) {
        return this.config.pressSealLid;
      }
      if (color === this.config.blackColor) {
        const handle = this.getProductHandle();
        if (handle === 'rux-40l') return this.config.pressSealLid;
        return this.config.triFoldLid; // rux-70l and default
      }
      return this.config.triFoldLid;
    }

    // The theme's SelloutVariants marks each color swatch by checking it against
    // the SINGLE currently-selected lid, so only the sold-out color compatible
    // with that lid gets the diagonal. Because color↔lid is 1:1 here, re-mark
    // each color swatch against its OWN paired-lid variant instead, so every
    // sold-out color shows the diagonal regardless of the current selection.
    applyColorSwatchSelloutMarks() {
      try {
        const productJsonElement = RUXUtils.safeQuery('[data-product-json]');
        if (!productJsonElement) return;

        const productData = JSON.parse(productJsonElement.textContent);
        if (!productData.variants || !Array.isArray(productData.variants)) return;

        const colorInputs = RUXUtils.safeQueryAll('input[name="options[Color]"]');
        colorInputs.forEach(input => {
          const color = input.value;
          const pairedLid = this.getPairedLidForColor(color);
          const variant = productData.variants.find(v => v.option1 === color && v.option2 === pairedLid);

          input.classList.remove('sold-out', 'unavailable');
          if (variant && variant.available === false) {
            input.classList.add('sold-out');
          }
          RUXUtils.log(`Swatch mark: ${color} (paired ${pairedLid}) → ${variant ? (variant.available ? 'available' : 'sold-out') : 'no-variant'}`);
        });
      } catch (error) {
        console.error('Lid Handler: Error applying color swatch sellout marks:', error);
      }
    }

    updateLidOptions(context, selectedColor) {
      // const isMarsRed = selectedColor === this.config.marsRedColor;
      // const isBronze = selectedColor === this.config.bronzeColor;
      // const targetLid = isMarsRed ? this.config.pressSealLid : isBronze ? this.config.pressSealLid this.config.triFoldLid;
      const targetLid = this.getPairedLidForColor(selectedColor);

      RUXUtils.log(`Lid Handler (${context.type}${context.bundleIndex ? ` bundle ${context.bundleIndex}` : ''}): Setting lid to ${targetLid} for color ${selectedColor}`);

      let selectedLidInput = null;
      let hiddenLidInputs = [];

      context.lidInputs.forEach(input => {
        const isTargetLid = input.value === targetLid;

        if (isTargetLid) {
          selectedLidInput = input;
        } else {
          hiddenLidInputs.push(input);
        }
      });

      // Hide unwanted inputs
      hiddenLidInputs.forEach(input => {
        const container = input.closest('.radio__button');

        input.checked = false;

        if (container && container.parentNode) {
          container.style.setProperty('display', 'none', 'important');
          container.style.setProperty('visibility', 'hidden', 'important');
          container.style.setProperty('opacity', '0', 'important');
          container.style.setProperty('pointer-events', 'none', 'important');
          container.style.setProperty('position', 'absolute', 'important');
          container.style.setProperty('z-index', '-9999', 'important');

          container.classList.add('rux-hidden');
          container.setAttribute('data-rux-hidden', 'true');
          container.setAttribute('aria-hidden', 'true');
        }

        input.disabled = true;
        input.style.setProperty('display', 'none', 'important');
        input.style.setProperty('visibility', 'hidden', 'important');
        input.setAttribute('aria-hidden', 'true');

        RUXUtils.log(`Hiding lid: ${input.value}`);
      });

      // Show and select target input
      if (selectedLidInput) {
        const container = selectedLidInput.closest('.radio__button');

        if (container) {
          container.classList.remove('rux-hidden');
          container.removeAttribute('data-rux-hidden');
          container.removeAttribute('aria-hidden');
          container.style.removeProperty('display');
          container.style.removeProperty('visibility');
          container.style.removeProperty('opacity');
          container.style.removeProperty('pointer-events');
          container.style.removeProperty('position');
          container.style.removeProperty('z-index');
        }

        selectedLidInput.disabled = false;
        selectedLidInput.style.removeProperty('display');
        selectedLidInput.style.removeProperty('visibility');
        selectedLidInput.removeAttribute('aria-hidden');

        selectedLidInput.classList.remove('unavailable', 'sold-out');
        if (container) {
          container.classList.remove('unavailable', 'sold-out');
        }

        selectedLidInput.checked = true;
        RUXUtils.log(`Showing and selecting lid: ${targetLid}`);
      }

      // Update lid display text
      if (context.lidDisplay) {
        context.lidDisplay.textContent = targetLid;
        RUXUtils.log(`Updated lid display text to: ${targetLid}`);
      }

      // For single products, update the variant
      if (context.type === 'single') {
        this.updateSingleProductVariant(selectedColor, targetLid);

        // Hand off to the theme's native ProductForm engine so it recomputes the
        // full variant state from the now-correct option radios: add-to-cart
        // availability, the .variant--soldout styling, the theme:variant:change
        // event that the Restock "Notify me" app listens for, price and the URL.
        const optionToSync = selectedLidInput || RUXUtils.safeQuery('input[name="options[Color]"]:checked');
        if (optionToSync) {
          optionToSync.dispatchEvent(new Event('change', { bubbles: true }));
          RUXUtils.log(`Lid Handler (single): Dispatched native change to sync theme engine (${optionToSync.name})`);
        }

        // Runs AFTER the theme's SelloutVariants (which fired synchronously during
        // the dispatch above) so our per-color paired-lid marks win.
        this.applyColorSwatchSelloutMarks();
      }

      this.notifyVariantUpdate(context, selectedColor, targetLid);
    }

    notifyVariantUpdate(context, selectedColor, selectedLid) {
      try {
        if (window.updateVariantIds) {
          setTimeout(() => {
            window.updateVariantIds();
          }, 100);
        }

        document.dispatchEvent(new CustomEvent('rux:lid:changed', {
          bubbles: false,
          detail: {
            context: context.type,
            bundleIndex: context.bundleIndex,
            color: selectedColor,
            lid: selectedLid
          }
        }));

        RUXUtils.log(`Lid Handler: Notified variant update - ${selectedColor} + ${selectedLid}`);

      } catch (error) {
        console.error('Error in notifyVariantUpdate:', error);
      }
    }

    updateSingleProductVariant(color, lid) {
      try {
        const productJsonElement = RUXUtils.safeQuery('[data-product-json]');
        if (!productJsonElement) {
          RUXUtils.log('No product JSON found for single product variant update');
          return;
        }

        const productData = JSON.parse(productJsonElement.textContent);
        if (!productData.variants || !Array.isArray(productData.variants)) {
          RUXUtils.log('Invalid product data structure');
          return;
        }

        const matchingVariant = productData.variants.find(variant =>
          variant.option1 === color && variant.option2 === lid
        );

        if (matchingVariant) {
          const variantIdInput = RUXUtils.safeQuery('input[name="id"][type="hidden"]');
          if (variantIdInput) {
            variantIdInput.value = matchingVariant.id;
            RUXUtils.log(`Updated variant ID to: ${matchingVariant.id} (available: ${matchingVariant.available})`);
          }

          // Add-to-cart enabled/disabled state, sold-out styling and the "Notify
          // me" button are now owned by the theme's native ProductForm engine,
          // driven by the change event dispatched in updateLidOptions. The
          // previous code here force-enabled the button for Black/Bronze/Mars Red
          // regardless of real stock, which masked out-of-stock variants.
          this.updatePriceDisplay(matchingVariant);
          RUXUtils.log('Lid Handler: Updated single product variant to', matchingVariant.title);

        } else {
          RUXUtils.log(`No matching variant found for ${color} + ${lid} - leaving availability to native engine`);
        }
      } catch (error) {
        console.error('Lid Handler: Error updating single product variant:', error);
      }
    }

    updatePriceDisplay(variant) {
      try {
        const priceElements = RUXUtils.safeQueryAll('.price, .money');
        const formattedPrice = RUXUtils.formatPrice(variant.price);

        priceElements.forEach(element => {
          if (element.closest('.product-single') || element.closest('.product__price')) {
            element.textContent = formattedPrice;
          }
        });

      } catch (error) {
        console.error('Error updating price display:', error);
      }
    }

    // ------------------------------------------------------------------------
    // INITIAL SETUP
    // ------------------------------------------------------------------------

    runInitialSetup() {
      RUXUtils.log('Lid Handler: Running initial setup for all contexts');

      this.activeContexts.forEach((context, key) => {
        try {
          this.runInitialSetupForContext(context);
        } catch (error) {
          console.error(`Error in initial setup for ${key}:`, error);
        }
      });
    }

    runInitialSetupForContext(context) {
      let selectedColorInput = null;

      if (context.type === 'single') {
        selectedColorInput = RUXUtils.safeQuery('input[name="options[Color]"]:checked');
      } else {
        selectedColorInput = RUXUtils.safeQuery(`input[name="options[bundle_${context.bundleIndex}_Color]"]:checked`);
      }

      if (selectedColorInput) {
        RUXUtils.log(`Running initial setup for ${context.type}${context.bundleIndex ? ` bundle ${context.bundleIndex}` : ''} with color: ${selectedColorInput.value}`);
        this.updateLidOptions(context, selectedColorInput.value);
      } else {
        RUXUtils.log(`No selected color found for ${context.type}${context.bundleIndex ? ` bundle ${context.bundleIndex}` : ''}`);
      }
    }
  }

  // ============================================================================
  // MARKET EXCLUSION HANDLER - Enhanced and organized
  // ============================================================================

  class RUXMarketExclusionHandler {
    constructor() {
      this.currentMarket = '';
      this.currentCountry = '';
      this.excludedVariants = new Set();
      this.isInitialized = false;

      this.init();
    }

    // ------------------------------------------------------------------------
    // INITIALIZATION
    // ------------------------------------------------------------------------

    init() {
      if (this.isInitialized || !this.hasProductGrid()) {
        if (!this.hasProductGrid()) {
          RUXUtils.log('Market Exclusion Handler: No product grid found, skipping');
        }
        return;
      }

      RUXUtils.log('Market Exclusion Handler: Initializing');

      this.detectMarket();

      if (!this.currentMarket && !this.currentCountry) {
        console.warn('Market Exclusion Handler: Could not detect market/country information');
        this.isInitialized = true;
        return;
      }

      this.collectExcludedVariants();
      this.applyExclusions();
      this.isInitialized = true;

      RUXUtils.log('Market Exclusion Handler: Successfully initialized');
    }

    hasProductGrid() {
      return !!RUXUtils.safeQuery('product-grid-item');
    }

    // ------------------------------------------------------------------------
    // MARKET DETECTION
    // ------------------------------------------------------------------------

    detectMarket() {
      this.currentMarket = this.getMarketFromMeta() || this.getMarketFromDebugData() || '';
      this.currentCountry = this.getCountryFromMeta() || this.getCountryFromDebugData() || '';

      this.currentMarket = this.currentMarket.toLowerCase().trim();
      this.currentCountry = this.currentCountry.toLowerCase().trim();

      RUXUtils.log('Market Exclusion Handler: Detected market/country:', {
        market: this.currentMarket,
        country: this.currentCountry
      });
    }

    getMarketFromMeta() {
      const meta = RUXUtils.safeQuery('meta[property="og:market"]');
      return meta?.content || '';
    }

    getCountryFromMeta() {
      const meta = RUXUtils.safeQuery('meta[property="og:country"]');
      return meta?.content || '';
    }

    getMarketFromDebugData() {
      const debugData = RUXUtils.safeQuery('#market-debug-data');
      const marketInfo = debugData?.querySelector('[data-market-info] [data-normalized-market]');
      return marketInfo?.textContent || '';
    }

    getCountryFromDebugData() {
      const debugData = RUXUtils.safeQuery('#market-debug-data');
      const countryInfo = debugData?.querySelector('[data-market-info] [data-normalized-country]');
      return countryInfo?.textContent || '';
    }

    // ------------------------------------------------------------------------
    // VARIANT COLLECTION
    // ------------------------------------------------------------------------

    collectExcludedVariants() {
      this.excludedVariants.clear();

      const debugData = RUXUtils.safeQuery('#market-debug-data');
      if (debugData) {
        RUXUtils.log('Market Exclusion Handler: Using debug data for variant collection');
        this.collectFromDebugData(debugData);
      } else {
        RUXUtils.log('Market Exclusion Handler: Using hardcoded rules for variant collection');
        this.collectFromHardcodedRules();
      }

      RUXUtils.log('Market Exclusion Handler: Collected variants to exclude:', Array.from(this.excludedVariants));
    }

    collectFromDebugData(debugData) {
      const products = debugData.querySelectorAll('[data-product]');

      products.forEach(product => {
        try {
          const singleVariant = product.querySelector('[data-single-variant]');
          if (singleVariant) {
            this.processSingleVariant(singleVariant);
          }

          const multipleVariants = product.querySelector('[data-multiple-variants]');
          if (multipleVariants) {
            const variants = multipleVariants.querySelectorAll('[data-variant]');
            variants.forEach(variant => this.processVariant(variant));
          }
        } catch (error) {
          console.error('Error processing product in debug data:', error);
        }
      });
    }

    processSingleVariant(variantElement) {
      try {
        const variantId = variantElement.querySelector('[data-variant-id]')?.textContent;
        const hasMetafield = variantElement.querySelector('[data-has-metafield]')?.textContent === 'true';

        if (!hasMetafield || !variantId) return;

        const shouldExclude = this.checkIfVariantShouldBeExcluded(variantElement);
        if (shouldExclude) {
          this.excludedVariants.add(variantId);
          RUXUtils.log(`Added single variant ${variantId} to exclusion list`);
        }
      } catch (error) {
        console.error('Error processing single variant:', error);
      }
    }

    processVariant(variantElement) {
      try {
        const variantId = variantElement.querySelector('[data-id]')?.textContent;
        const hasMetafield = variantElement.querySelector('[data-has-metafield]')?.textContent === 'true';

        if (!hasMetafield || !variantId) return;

        const shouldExclude = this.checkIfVariantShouldBeExcluded(variantElement);
        if (shouldExclude) {
          this.excludedVariants.add(variantId);
          RUXUtils.log(`Added variant ${variantId} to exclusion list`);
        }
      } catch (error) {
        console.error('Error processing variant:', error);
      }
    }

    checkIfVariantShouldBeExcluded(variantElement) {
      try {
        const excludedMarkets = variantElement.querySelectorAll('[data-excluded-market]');

        return Array.from(excludedMarkets).some(market => {
          const containsMarket = market.querySelector('[data-contains-market]')?.textContent?.trim() === 'true';
          const containsCountry = market.querySelector('[data-contains-country]')?.textContent?.trim() === 'true';
          return containsMarket || containsCountry;
        });
      } catch (error) {
        console.error('Error checking variant exclusion:', error);
        return false;
      }
    }

    collectFromHardcodedRules() {
      const rules = {
        canada: ['44579508650222', '44579508617454', '44579508682990', '44579499835630', '44579499933934', '44579499868398'],
        ca: ['44579508650222', '44579508617454', '44579508682990', '44579499835630', '44579499933934', '44579499868398'],
        usa: ['44579499933934', '44579499868398'],
        us: ['44579499933934', '44579499868398'],
        'united states': ['44579499933934', '44579499868398'],
        'united states of america': ['44579499933934', '44579499868398']
      };

      const applicableRule = rules[this.currentCountry] || rules[this.currentMarket];
      if (applicableRule) {
        applicableRule.forEach(variantId => {
          this.excludedVariants.add(variantId);
        });
        RUXUtils.log(`Applied hardcoded rule for ${this.currentCountry || this.currentMarket}:`, applicableRule);
      } else {
        RUXUtils.log(`No hardcoded rules found for country: "${this.currentCountry}" or market: "${this.currentMarket}"`);
      }
    }

    // ------------------------------------------------------------------------
    // EXCLUSION APPLICATION
    // ------------------------------------------------------------------------

    applyExclusions() {
      if (this.excludedVariants.size === 0) {
        RUXUtils.log('Market Exclusion Handler: No variants to exclude');
        return;
      }

      const stats = {
        variantsHidden: 0,
        swatchesHidden: 0,
        productsHidden: 0,
        startTime: performance.now()
      };

      RUXUtils.log(`Market Exclusion Handler: Starting exclusion of ${this.excludedVariants.size} variants`);

      this.hideVariantElements(stats);
      this.hideSwatches(stats);

      setTimeout(() => {
        this.hideEmptyProducts(stats);
        this.logCompletionStats(stats);
      }, 100);
    }

    hideVariantElements(stats) {
      const selectors = [
        'product-grid-item-variant[data-grid-item-variant]',
        '[data-grid-item-variant]',
        'a[data-grid-item-variant]',
        'a[data-sibling-swatch-link][data-grid-item-variant]'
      ];

      selectors.forEach(selector => {
        try {
          RUXUtils.safeQueryAll(selector).forEach(element => {
            const variantId = element.getAttribute('data-grid-item-variant');

            if (variantId && this.excludedVariants.has(variantId)) {
              this.hideElement(element, 'market-excluded-variant');
              stats.variantsHidden++;

              if (window.RUXTheme.debug) {
                RUXUtils.log(`Hidden variant element: ${variantId} (${selector})`);
              }
            }
          });
        } catch (error) {
          console.error(`Error processing selector ${selector}:`, error);
        }
      });
    }

    hideSwatches(stats) {
      const swatchSelectors = [
        '.swatch__button[data-grid-item-variant]',
        'radio-swatch[data-grid-item-variant]',
        '.siblings__link__holder[data-grid-item-variant]',
        'a[data-swatch][data-grid-item-variant]',
        '.color-swatch[data-grid-item-variant]'
      ];

      swatchSelectors.forEach(selector => {
        try {
          RUXUtils.safeQueryAll(selector).forEach(swatch => {
            const variantId = swatch.getAttribute('data-grid-item-variant');

            if (variantId && this.excludedVariants.has(variantId)) {
              this.hideElement(swatch, 'market-excluded-swatch');
              stats.swatchesHidden++;

              if (window.RUXTheme.debug) {
                RUXUtils.log(`Hidden swatch element: ${variantId} (${selector})`);
              }
            }
          });
        } catch (error) {
          console.error(`Error processing swatch selector ${selector}:`, error);
        }
      });
    }

    hideEmptyProducts(stats) {
      RUXUtils.safeQueryAll('product-grid-item').forEach((productElement, index) => {
        try {
          const variantElements = productElement.querySelectorAll('[data-grid-item-variant]');

          if (variantElements.length === 0) {
            return;
          }

          const allVariantsHidden = Array.from(variantElements).every(variant =>
            variant.hasAttribute('data-market-excluded') ||
            variant.style.display === 'none' ||
            this.isElementHidden(variant)
          );

          if (allVariantsHidden) {
            this.hideElement(productElement, 'market-excluded-product');
            stats.productsHidden++;

            if (window.RUXTheme.debug) {
              RUXUtils.log(`Hidden product ${index + 1} - all variants excluded`);
            }
          }
        } catch (error) {
          console.error(`Error processing product ${index}:`, error);
        }
      });
    }

    hideElement(element, className) {
      element.style.display = 'none';
      element.setAttribute('data-market-excluded', 'true');
      element.classList.add(className);
      element.setAttribute('aria-hidden', 'true');
    }

    isElementHidden(element) {
      return element.style.display === 'none' ||
        element.hasAttribute('data-market-excluded') ||
        element.offsetParent === null;
    }

    logCompletionStats(stats) {
      const duration = performance.now() - stats.startTime;
      RUXUtils.log('Market Exclusion Handler: Exclusion complete', {
        ...stats,
        duration: `${duration.toFixed(2)}ms`,
        excludedVariants: Array.from(this.excludedVariants)
      });
    }

    // ------------------------------------------------------------------------
    // DEBUG & REFRESH METHODS
    // ------------------------------------------------------------------------

    debugMarketInfo() {
      if (!window.RUXTheme.debug) {
        console.log('Enable debug mode with ?debug=true to see detailed market info');
        return;
      }

      console.log('=== MARKET EXCLUSION DEBUG ===');
      console.log('Current Market:', this.currentMarket);
      console.log('Current Country:', this.currentCountry);
      console.log('Excluded Variants:', Array.from(this.excludedVariants));

      const totalProducts = RUXUtils.safeQueryAll('product-grid-item').length;
      const hiddenProducts = RUXUtils.safeQueryAll('product-grid-item[data-market-excluded]').length;
      console.log(`Products: ${totalProducts - hiddenProducts}/${totalProducts} visible`);

      const totalVariants = RUXUtils.safeQueryAll('[data-grid-item-variant]').length;
      const hiddenVariants = RUXUtils.safeQueryAll('[data-grid-item-variant][data-market-excluded]').length;
      console.log(`Variants: ${totalVariants - hiddenVariants}/${totalVariants} visible`);

      this.excludedVariants.forEach(variantId => {
        const elements = RUXUtils.safeQueryAll(`[data-grid-item-variant="${variantId}"]`);
        console.log(`Variant ${variantId}: ${elements.length} elements found`);
      });
    }

    refresh() {
      RUXUtils.log('Market Exclusion Handler: Refreshing exclusions');
      this.excludedVariants.clear();

      RUXUtils.safeQueryAll('[data-market-excluded]').forEach(element => {
        element.style.display = '';
        element.removeAttribute('data-market-excluded');
        element.removeAttribute('aria-hidden');
        element.classList.remove('market-excluded-variant', 'market-excluded-swatch', 'market-excluded-product');
      });

      this.collectExcludedVariants();
      this.applyExclusions();
    }
  }

  // ============================================================================
  // VARIANT IMAGE HANDLER - CSS Scroll-Snap based (No Flickity dependency)
  // ============================================================================

  /**
   * RUXVariantImageHandler - Refactored for CSS Scroll-Snap
   *
   * Handles:
   * - Variant-based image filtering via alt text patterns
   * - Mobile scroll-snap navigation with dot indicators
   * - Thumbnail synchronization
   * - Desktop grid (no carousel control needed)
   *
   * NO Flickity dependencies
   */
  class RUXVariantImageHandler {
    constructor() {
      this.slideshowContainer = document.querySelector('[data-product-slideshow]');
      this.thumbnailsContainer = document.querySelector('[data-product-thumbs]');
      this.variantsContainer = document.querySelector('.product__block__variants') || document;

      // Option dimensions, most-significant first, read from the rendered controls
      this.dimensions = [];
      this.optionWrappers = [];
      this.detectVariantOptions();

      // Scroll-snap state
      this.currentIndex = 0;
      this.visibleSlides = [];
      this.dotsContainer = null;
      this.intersectionObserver = null;
      this.isScrolling = false;

      // Breakpoint detection
      this.isMobile = window.matchMedia('(max-width: 767px)');

      this.init();
    }

    /**
     * Dimension order is read from the rendered markup, never assumed - the
     * fallback chain walks it backwards, so an option reordered in the admin
     * has to change which dimension gets dropped first.
     *
     * The server's selected scope is the source: it lists every option of the
     * product, most significant first, including any whose control is not
     * rendered. Rendered controls fill in for markup without that attribute.
     */
    detectVariantOptions() {
      // rux-media-scope.js is a separate asset loaded ahead of this one. If it
      // is missing, dereferencing it here throws, and because this handler is
      // constructed last that throw also skips the window.updateVariantIds
      // assignment below, which bundle call sites depend on. Degrade to
      // server-rendered media instead of taking the bundle PDPs down with us.
      if (typeof RUXMediaScope === 'undefined') {
        RUXUtils.log('VariantImageHandler: RUXMediaScope asset missing, skipping');
        this.slideshowContainer = null;
        return;
      }

      this.dimensions = RUXMediaScope.parseScopeOrder(
        this.slideshowContainer ? this.slideshowContainer.getAttribute('data-selected-scope') : ''
      );
      this.optionWrappers = Array.from(
        this.variantsContainer.querySelectorAll('[data-option-handle]')
      ).filter(wrapper => {
        // Bundle PDPs are out of scope: their inputs are named options[bundle_N_...]
        const input = wrapper.querySelector('input[name^="options["]');
        return !input || !input.name.startsWith('options[bundle_');
      });

      this.optionWrappers.forEach(wrapper => {
        const handle = wrapper.dataset.optionHandle;
        if (handle && !this.dimensions.includes(handle)) {
          this.dimensions.push(handle);
        }
      });

      RUXUtils.log('VariantImageHandler: Dimensions:', this.dimensions);
    }

    init() {
      if (!this.slideshowContainer) {
        RUXUtils.log('VariantImageHandler: No slideshow container found, skipping');
        return;
      }

      // Bind variant change listeners. Popout selects fire `change` on their
      // hidden input, so both control styles have to be bound.
      this.optionWrappers.forEach(wrapper => {
        wrapper.querySelectorAll('input[type="radio"], [data-popout-input]')
          .forEach(input => input.addEventListener('change', () => this.handleFilterChange()));
      });

      // The theme fires this after its own variant handling. It bubbles, so a
      // document listener runs after the section's, which is what makes this a
      // backstop against the vendored bundle's dead image system unhiding media
      // on a variant change.
      document.addEventListener('theme:variant:change', () => this.applyScope());

      // Setup thumbnail click handlers
      this.setupThumbnailClicks();

      // Setup mobile scroll-snap if on mobile
      if (this.isMobile.matches) {
        this.setupMobileScrollSnap();
      }

      // Handle resize/breakpoint changes
      this.setupResizeHandler();

      // Initial filter application
      this.handleFilterChange();

      RUXUtils.log('VariantImageHandler: Initialized');
    }

    // ==========================================
    // MOBILE SCROLL-SNAP FUNCTIONALITY
    // ==========================================

    setupMobileScrollSnap() {
      if (!this.slideshowContainer) return;

      // Create dot indicators
      this.createDotIndicators();

      // Setup scroll listener for dot sync
      this.slideshowContainer.addEventListener('scroll',
        RUXUtils.debounce(() => this.handleScroll(), 100),
        { passive: true }
      );

      // Setup IntersectionObserver for precise slide detection
      this.setupIntersectionObserver();

      RUXUtils.log('VariantImageHandler: Mobile scroll-snap initialized');
    }

    createDotIndicators() {
      // Find or create dots container
      const existingDots = this.slideshowContainer.parentElement?.querySelector('.product__gallery-dots');
      if (existingDots) {
        existingDots.remove();
      }

      this.dotsContainer = document.createElement('div');
      this.dotsContainer.className = 'product__gallery-dots';
      this.dotsContainer.setAttribute('role', 'tablist');
      this.dotsContainer.setAttribute('aria-label', 'Image navigation');

      // Insert after slideshow container
      if (this.slideshowContainer.parentElement) {
        this.slideshowContainer.parentElement.style.position = 'relative';
        this.slideshowContainer.parentElement.appendChild(this.dotsContainer);
      }

      this.updateDots();
    }

    updateDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';

      this.visibleSlides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.className = 'product__gallery-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', index === this.currentIndex ? 'true' : 'false');
        dot.setAttribute('aria-label', `Go to image ${index + 1}`);

        if (index === this.currentIndex) {
          dot.classList.add('is-active');
        }

        dot.addEventListener('click', () => this.scrollToSlide(index));

        this.dotsContainer.appendChild(dot);
      });
    }

    setupIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      const options = {
        root: this.slideshowContainer,
        rootMargin: '0px',
        threshold: 0.6
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (this.isScrolling) return;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const slide = entry.target;
            const index = this.visibleSlides.indexOf(slide);
            if (index !== -1 && index !== this.currentIndex) {
              this.currentIndex = index;
              this.updateDots();
              this.syncThumbnail(slide);
            }
          }
        });
      }, options);

      this.visibleSlides.forEach(slide => {
        this.intersectionObserver.observe(slide);
      });
    }

    handleScroll() {
      if (this.isScrolling) return;

      const containerRect = this.slideshowContainer.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      this.visibleSlides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(containerCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
        this.updateDots();
        this.syncThumbnail(this.visibleSlides[closestIndex]);
      }
    }

    scrollToSlide(index) {
      const slide = this.visibleSlides[index];
      if (!slide) return;

      this.isScrolling = true;
      this.currentIndex = index;

      this.scrollGalleryTo(slide, 'smooth');

      this.updateDots();
      this.syncThumbnail(slide);

      // Reset scrolling flag after animation
      setTimeout(() => {
        this.isScrolling = false;
      }, 500);
    }

    /**
     * Scrolls the gallery track only. scrollIntoView also scrolls every
     * scrollable ancestor, including the page: with sections above the
     * product, the reset on load dragged the viewport down to the gallery.
     */
    scrollGalleryTo(slide, behavior) {
      const container = this.slideshowContainer;
      const offset = slide.getBoundingClientRect().left - container.getBoundingClientRect().left;
      const centring = (container.clientWidth - slide.offsetWidth) / 2;

      container.scrollTo({ left: container.scrollLeft + offset - centring, behavior });
    }

    cleanupMobileScrollSnap() {
      if (this.dotsContainer) {
        this.dotsContainer.remove();
        this.dotsContainer = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    // ==========================================
    // THUMBNAIL HANDLING
    // ==========================================

    setupThumbnailClicks() {
      if (!this.thumbnailsContainer) return;

      const thumbnails = this.thumbnailsContainer.querySelectorAll('[data-slideshow-thumbnail]');

      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', (e) => {
          e.preventDefault();
          const mediaId = thumbnail.getAttribute('data-media-select');
          this.selectSlideByMediaId(mediaId);
        });
      });
    }

    selectSlideByMediaId(mediaId) {
      const targetSlide = this.slideshowContainer.querySelector(
        `[data-media-slide][data-media-id="${mediaId}"]:not(.hide)`
      );

      if (!targetSlide) return;

      const index = this.visibleSlides.indexOf(targetSlide);
      if (index !== -1) {
        if (this.isMobile.matches) {
          this.scrollToSlide(index);
        } else {
          // Desktop: just sync thumbnail highlight
          this.currentIndex = index;
          this.syncThumbnail(targetSlide);
        }
      }
    }

    syncThumbnail(slide) {
      if (!this.thumbnailsContainer || !slide) return;

      const mediaId = slide.getAttribute('data-media-id');
      const thumbnails = this.thumbnailsContainer.querySelectorAll('[data-slideshow-thumbnail]');

      thumbnails.forEach(thumb => {
        thumb.classList.remove('is-activated');
        if (thumb.getAttribute('data-media-select') === mediaId) {
          thumb.classList.add('is-activated');

          // Scroll thumbnail into view
          thumb.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });
    }

    // ==========================================
    // RESIZE HANDLING
    // ==========================================

    setupResizeHandler() {
      this.isMobile.addEventListener('change', (e) => {
        if (e.matches) {
          // Switched to mobile
          this.setupMobileScrollSnap();
        } else {
          // Switched to desktop
          this.cleanupMobileScrollSnap();
        }
        this.handleFilterChange();
      });
    }

    // ==========================================
    // VARIANT FILTERING (PRESERVED LOGIC)
    // ==========================================

    /**
     * Current selection as { dimension: valueHandle }. Handles come from
     * Liquid (`data-value-handle`), so nothing is re-derived here - the old
     * toLowerCase/replace normalization silently diverged from `handleize` on
     * any value carrying punctuation.
     */
    getSelection() {
      // Seed from the server's selection so dimensions with no rendered
      // control (a market-excluded value, say) keep the value Liquid scoped
      // the media to. Rendered controls override it below.
      const selection = RUXMediaScope.parseScope(
        this.slideshowContainer ? this.slideshowContainer.getAttribute('data-selected-scope') : ''
      );

      this.optionWrappers.forEach(wrapper => {
        const dim = wrapper.dataset.optionHandle;
        if (!dim) return;

        const checked = wrapper.querySelector('input[type="radio"]:checked');
        if (checked && checked.dataset.valueHandle) {
          selection[dim] = checked.dataset.valueHandle;
          return;
        }

        // Popout select: the hidden input holds the raw value; the anchor holds the handle
        const hidden = wrapper.querySelector('[data-popout-input]');
        if (hidden && hidden.value) {
          const anchor = Array.from(
            wrapper.querySelectorAll('[data-popout-option]')
          ).find(a => a.dataset.value === hidden.value);
          if (anchor && anchor.dataset.valueHandle) {
            selection[dim] = anchor.dataset.valueHandle;
          }
        }
      });

      RUXUtils.log('VariantImageHandler: Selection:', selection);
      return selection;
    }

    handleFilterChange() {
      // Apply scope matching to slides and thumbnails
      this.applyScope();

      // Update visible slides array
      this.updateVisibleSlides();

      // Reset to first visible slide
      this.resetToFirstSlide();

      // Update mobile UI if active
      if (this.isMobile.matches && this.dotsContainer) {
        this.updateDots();
        this.refreshIntersectionObserver();
      }
    }

    /**
     * Show exactly the media the current selection scopes to, in both the grid
     * and the thumbnail rail. Mirrors snippets/product-media-column.liquid.
     *
     * The fallback chain is resolved once, off the slides, and the resulting
     * ignoredDimensions is reused for the thumbnails - resolving twice would
     * let the two rails disagree whenever a combination is unshot.
     */
    applyScope() {
      if (!this.slideshowContainer) return;

      const selection = this.getSelection();

      const slides = Array.from(
        this.slideshowContainer.querySelectorAll('[data-media-slide]')
      );
      const items = slides.map(el => ({ el, scope: el.getAttribute('data-media-scope') || '' }));

      const { ignoredDimensions } = RUXMediaScope.resolveVisible(items, selection, this.dimensions);

      const setVisible = (el, show) => {
        el.classList.toggle('hide', !show);
        el.style.display = show ? '' : 'none';
        if (show) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', 'true');
        }
        // Keeps the vendored bundle's dead GroupVariantImages a no-op - see the
        // comment on data-image-filter in snippets/media.liquid. Nothing here
        // reads it back.
        el.setAttribute('data-image-filter', show ? '' : 'all');
      };

      slides.forEach(el => {
        setVisible(el, RUXMediaScope.matchesScope(
          el.getAttribute('data-media-scope') || '', selection, ignoredDimensions
        ));
      });

      if (this.thumbnailsContainer) {
        this.thumbnailsContainer
          .querySelectorAll('[data-slideshow-thumbnail]')
          .forEach(el => {
            setVisible(el, RUXMediaScope.matchesScope(
              el.getAttribute('data-media-scope') || '', selection, ignoredDimensions
            ));
          });
      }

      // Visibility alone leaves a newly selected variant's media wherever the
      // server put it for the previous selection - after the shared media.
      // Re-sort to the server's order: matching media first, admin order
      // within each group.
      this.reorderByScope(this.slideshowContainer, '[data-media-slide]');
      if (this.thumbnailsContainer) {
        this.reorderByScope(this.thumbnailsContainer, '[data-slideshow-thumbnail]');
      }

      RUXUtils.log('VariantImageHandler: Ignored dimensions:', ignoredDimensions);
    }

    reorderByScope(container, selector) {
      const items = Array.from(container.querySelectorAll(`:scope > ${selector}`));
      const position = el => Number(el.getAttribute('data-media-position')) || 0;
      const sorted = items.slice().sort((a, b) =>
        (a.classList.contains('hide') - b.classList.contains('hide')) || (position(a) - position(b))
      );

      if (sorted.every((el, i) => el === items[i])) return;
      sorted.forEach(el => container.appendChild(el));
    }

    updateVisibleSlides() {
      // refresh() reaches here from window.updateVariantIds, which bundle call
      // sites invoke without knowing whether this handler stood itself down.
      if (!this.slideshowContainer) {
        this.visibleSlides = [];
        return;
      }
      this.visibleSlides = Array.from(
        this.slideshowContainer.querySelectorAll('[data-media-slide]:not(.hide)')
      );
    }

    resetToFirstSlide() {
      if (this.visibleSlides.length === 0) return;

      this.currentIndex = 0;
      const firstSlide = this.visibleSlides[0];

      // On mobile, scroll to first slide
      if (this.isMobile.matches) {
        this.scrollGalleryTo(firstSlide, 'instant');
      }

      // Sync thumbnail
      this.syncThumbnail(firstSlide);
    }

    refreshIntersectionObserver() {
      if (!this.intersectionObserver) return;

      this.intersectionObserver.disconnect();

      this.visibleSlides.forEach(slide => {
        this.intersectionObserver.observe(slide);
      });
    }

    // Public method to manually trigger refresh
    refresh() {
      this.handleFilterChange();
    }

    // Public method for cleanup
    destroy() {
      this.cleanupMobileScrollSnap();
    }
  }

  // ============================================================================
  // MAIN INITIALIZATION & GLOBAL FUNCTIONS
  // ============================================================================

  /**
   * Main initialization function
   */
  function initializeRUXTheme() {
    if (window.RUXTheme.initialized) {
      RUXUtils.log('RUX Theme already initialized, skipping');
      return;
    }

    window.RUXTheme.initialized = true;
    RUXUtils.log('RUX Theme: Initializing Complete Modern System');

    try {
      // Initialize core systems
      window.RUXTheme.cartSystem = new RUXCartSystem();
      window.RUXTheme.handlers.set('bundle', new RUXBundleHandler());
      window.RUXTheme.handlers.set('lidHandler', new RUXLidHandler());
      window.RUXTheme.handlers.set('marketExclusion', new RUXMarketExclusionHandler());
      window.RUXTheme.handlers.set('variantImages', new RUXVariantImageHandler());

      // Make updateVariantIds available globally
      window.updateVariantIds = function () {
        const bundleHandler = window.RUXTheme.handlers.get('bundle');
        if (bundleHandler && typeof bundleHandler.runBundleCalculation === 'function') {
          bundleHandler.runBundleCalculation();
        }
        const imageHandler = window.RUXTheme.handlers.get('variantImages');
        if (imageHandler && typeof imageHandler.refresh === 'function') {
          imageHandler.refresh();
        }
      };

      RUXUtils.log('RUX Theme: All systems initialized successfully');

    } catch (error) {
      console.error('RUX Theme: Initialization failed:', error);
    }
  }

  /**
   * Enhanced debug functions for development
   */
  function setupDebugFunctions() {
    if (!window.RUXTheme.debug) return;

    // Cart debugging
    window.debugCart = function () {
      const cart = window.RUXTheme.cartSystem;
      console.log('=== CART DEBUG ===');
      console.log('Cart drawer:', cart?.cartDrawer);
      console.log('Cart classes:', cart?.cartDrawer?.className);
      console.log('Is processing:', cart?.isProcessing);
      console.log('Event listeners:', cart?.eventListeners?.size);
    };

    // Lid handler debugging
    window.debugLidHandler = function () {
      const lidHandler = window.RUXTheme.handlers.get('lidHandler');
      console.log('=== LID HANDLER DEBUG ===');
      console.log('Page type:', lidHandler?.pageType);
      console.log('Initialized:', lidHandler?.isInitialized);
      console.log('Active contexts:', lidHandler?.activeContexts?.size);
      console.log('Config:', lidHandler?.config);
    };

    // Market exclusion debugging
    window.debugMarketExclusion = function () {
      const marketHandler = window.RUXTheme.handlers.get('marketExclusion');
      if (marketHandler && typeof marketHandler.debugMarketInfo === 'function') {
        marketHandler.debugMarketInfo();
      } else {
        console.log('Market exclusion handler not available');
      }
    };

    // Bundle debugging
    window.debugBundle = function () {
      const bundleHandler = window.RUXTheme.handlers.get('bundle');
      console.log('=== BUNDLE DEBUG ===');
      console.log('Initialized:', bundleHandler?.isInitialized);
      console.log('Has bundles:', bundleHandler?.hasBundles());

      // Test bundle calculation
      if (bundleHandler && typeof bundleHandler.runBundleCalculation === 'function') {
        console.log('Running bundle calculation...');
        bundleHandler.runBundleCalculation();
      }
    };

    // Refresh functions
    window.refreshMarketExclusions = function () {
      const marketHandler = window.RUXTheme.handlers.get('marketExclusion');
      if (marketHandler && typeof marketHandler.refresh === 'function') {
        marketHandler.refresh();
      } else {
        console.log('Market exclusion handler not available');
      }
    };

    // Test all systems
    window.testAllSystems = function () {
      console.log('=== TESTING ALL RUX SYSTEMS ===');

      debugCart();
      debugLidHandler();
      debugMarketExclusion();
      debugBundle();
      debugUndefinedItems();

      console.log('=== TEST COMPLETE ===');
    };

    RUXUtils.log('Debug functions available:', [
      'debugCart()', 'debugLidHandler()', 'debugMarketExclusion()',
      'debugBundle()', 'refreshMarketExclusions()', 'testCartOpen()', 'testAllSystems()'
    ]);
  }

  /**
   * Cleanup function for page unload
   */
  function cleanup() {
    try {
      if (window.RUXTheme.cartSystem && typeof window.RUXTheme.cartSystem.destroy === 'function') {
        window.RUXTheme.cartSystem.destroy();
      }

      window.RUXTheme.handlers.clear();
      RUXUtils.log('RUX Theme: Cleanup completed');

    } catch (error) {
      console.error('RUX Theme: Cleanup failed:', error);
    }
  }

  // ============================================================================
  // STARTUP SEQUENCE
  // ============================================================================

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeRUXTheme();
      setupDebugFunctions();
    }, { once: true });
  } else {
    // DOM is already ready
    initializeRUXTheme();
    setupDebugFunctions();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanup, { once: true });

  // Make RUXTheme globally accessible for debugging
  window.RUXTheme.utils = RUXUtils;
  window.RUXTheme.cleanup = cleanup;
  window.RUXTheme.reinitialize = function () {
    cleanup();
    window.RUXTheme.initialized = false;
    initializeRUXTheme();
    setupDebugFunctions();
  };

  RUXUtils.log('RUX Theme: Script loaded and ready');

})();


// CURRENCY-AWARE PRODUCT PAGE PRICE FIX
// Detects current market/currency and formats accordingly

(function () {
  'use strict';

  // Function to detect current currency
  function getCurrentCurrency() {
    // Try multiple methods to detect currency

    // Method 1: From theme/Shopify variables
    if (window.theme?.currencyCode) {
      return window.theme.currencyCode;
    }

    // Method 2: From Shopify.currency if available
    if (window.Shopify?.currency?.active) {
      return window.Shopify.currency.active;
    }

    // Method 3: From localization data (from your variant options snippet)
    const currencyMetaElement = document.querySelector('meta[name="currency"]');
    if (currencyMetaElement) {
      return currencyMetaElement.content;
    }

    // Method 4: From existing price elements on page
    const existingPriceElement = document.querySelector('.money, [data-product-price]');
    if (existingPriceElement) {
      const priceText = existingPriceElement.textContent;
      const currencyMatch = priceText.match(/\b(USD|CAD|EUR|GBP|CHF|SEK|DKK|NOK)\b/i);
      if (currencyMatch) {
        return currencyMatch[1].toUpperCase();
      }
    }

    // Method 5: From URL or domain (fallback)
    const hostname = window.location.hostname;
    if (hostname.includes('.ca')) return 'CAD';
    if (hostname.includes('.eu')) return 'EUR';
    if (hostname.includes('.uk')) return 'GBP';

    // Method 6: Check for currency in localization (from your snippet)
    const scriptTags = document.querySelectorAll('script');
    for (const script of scriptTags) {
      if (script.textContent.includes('currency_iso_code')) {
        const match = script.textContent.match(/currency_iso_code\s*=\s*['"]([A-Z]{3})['"]/);
        if (match) {
          return match[1];
        }
      }
    }

    // Default fallback
    return 'USD';
  }

  // Function to format price with detected currency
  function formatPriceWithCurrency(priceInCents, currencyCode) {
    try {
      // First try Shopify's formatMoney if available
      if (window.Shopify?.formatMoney) {
        // Use theme's money format settings
        const currencyFormat = window.theme?.moneyFormats?.money_with_currency_format ||
          window.theme?.moneyFormat;

        if (currencyFormat) {
          return Shopify.formatMoney(priceInCents, currencyFormat);
        }
      }

      // Fallback: Manual formatting with detected currency
      const price = (priceInCents / 100).toFixed(2);

      // Currency symbols and formats
      const currencyFormats = {
        'USD': `$${price}`,
        'CAD': `$${price} CAD`,
        'EUR': `€${price}`,
        'GBP': `£${price}`,
        'CHF': `CHF ${price}`,
        'SEK': `${price} kr`,
        'DKK': `${price} DKK`,
        'NOK': `${price} NOK`
      };

      const formattedPrice = currencyFormats[currencyCode] || `${price} ${currencyCode}`;
      console.log(`💰 Formatted ${priceInCents} cents as "${formattedPrice}" for ${currencyCode}`);

      return formattedPrice;

    } catch (error) {
      console.error('Error formatting price:', error);
      const price = (priceInCents / 100).toFixed(2);
      return `${price} ${currencyCode}`;
    }
  }

  function updateProductPagePrices() {
    // Skip if this is a bundle - bundle handler will manage prices
    const isBundlePage = document.querySelector('[data-bundle-variant-id]');
    if (isBundlePage) {
      console.log('🎁 Bundle detected - skipping global price updater (bundle handler will manage)');
      return;
    }

    const productPageContainer = document.querySelector('div.product-page');
    if (!productPageContainer) {
      console.log('🚫 No product-page container found');
      return;
    }

    const variantIdInput = document.querySelector('input[name="id"][type="hidden"]');
    const currentVariantId = variantIdInput?.value;
    if (!currentVariantId) {
      console.log('🚫 No variant ID found');
      return;
    }

    const productJsonElement = document.querySelector('[data-product-json]');
    if (!productJsonElement) {
      console.log('🚫 No product JSON found');
      return;
    }

    let productData;
    try {
      productData = JSON.parse(productJsonElement.textContent);
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
      return;
    }

    const currentVariant = productData.variants?.find(v => v.id == currentVariantId);
    if (!currentVariant) {
      console.log('🚫 Current variant not found:', currentVariantId);
      return;
    }

    // Detect current currency
    const currentCurrency = getCurrentCurrency();
    console.log(`💱 Detected currency: ${currentCurrency}`);

    // Format price with correct currency
    const formattedPrice = formatPriceWithCurrency(currentVariant.price, currentCurrency);
    const formattedComparePrice = currentVariant.compare_at_price ?
      formatPriceWithCurrency(currentVariant.compare_at_price, currentCurrency) : null;

    console.log(`🎯 Updating prices for variant: ${currentVariant.title}`);
    console.log(`💰 New price: ${formattedPrice}`);
    if (formattedComparePrice) {
      console.log(`💸 Compare price: ${formattedComparePrice}`);
    }

    let totalUpdated = 0;

    // Find all price containers within product-page
    const priceContainers = [];
    const selectors = [
      '.product__price__main',
      '.product__block__price',
      '[data-price-wrapper]',
      '.product-single__price',
      '.price-wrapper',
      '.product__price',
      '.price'
    ];

    selectors.forEach(selector => {
      const elements = productPageContainer.querySelectorAll(selector);
      elements.forEach(el => {
        if (!priceContainers.some(container => container.contains(el) || el.contains(container))) {
          priceContainers.push(el);
        }
      });
    });

    // Also update button prices
    const buttonPriceElements = productPageContainer.querySelectorAll('[data-button-price]');
    buttonPriceElements.forEach((buttonPrice, index) => {
      const oldButtonPrice = buttonPrice.textContent;
      if (oldButtonPrice !== formattedPrice) {
        buttonPrice.textContent = formattedPrice;
        console.log(`🔘 Updated button price ${index + 1}: "${oldButtonPrice}" → "${formattedPrice}"`);
        totalUpdated++;
      }
    });

    // Update each price container
    priceContainers.forEach((container, index) => {
      console.log(`📱💻 Updating container ${index + 1}`);

      // Update main price elements
      const mainPriceSelectors = [
        '[data-product-price]',
        '[data-button-price]',
        '.product__price:not([data-price-discount]):not([data-bundle-price]):not([data-preorder-price])',
        '.price:not(.compare-price):not(.was-price)',
        '.money:not(.compare-at-money)'
      ];

      let mainPriceElement = null;
      for (const selector of mainPriceSelectors) {
        mainPriceElement = container.querySelector(selector);
        if (mainPriceElement) break;
      }

      if (mainPriceElement) {
        const oldPrice = mainPriceElement.textContent;
        if (oldPrice !== formattedPrice) {
          mainPriceElement.textContent = formattedPrice;
          console.log(`   ✅ Updated: "${oldPrice}" → "${formattedPrice}"`);
          totalUpdated++;
        }

        // Handle sale class
        if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
          mainPriceElement.classList.add('product__price--sale');
        } else {
          mainPriceElement.classList.remove('product__price--sale');
        }
      }

      // Update compare price (including button preorder price)
      const comparePriceSelectors = [
        '[data-price-discount]',
        '[data-button-price-preorder]',
        '.compare-price',
        '.was-price'
      ];

      let comparePriceElement = null;
      for (const selector of comparePriceSelectors) {
        comparePriceElement = container.querySelector(selector);
        if (comparePriceElement) break;
      }

      if (comparePriceElement && formattedComparePrice) {
        if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
          comparePriceElement.textContent = formattedComparePrice;
          comparePriceElement.style.display = '';
          console.log(`   💸 Updated compare: "${formattedComparePrice}"`);
        } else {
          comparePriceElement.style.display = 'none';
        }
      }

      // Update other .money elements
      const moneyElements = container.querySelectorAll('.money');
      moneyElements.forEach((moneyEl, i) => {
        if (!moneyEl.closest('[data-price-discount]') &&
          !moneyEl.closest('.compare-price') &&
          !moneyEl.closest('[data-preorder-price]')) {
          const oldMoney = moneyEl.textContent;
          if (oldMoney !== formattedPrice) {
            moneyEl.textContent = formattedPrice;
            console.log(`   💵 Updated money element: "${oldMoney}" → "${formattedPrice}"`);
            totalUpdated++;
          }
        }
      });
    });

    console.log(`✅ Updated ${totalUpdated} price elements (Currency: ${currentCurrency})`);
  }

  // Debounced update function
  let updateTimeout;
  function debouncedUpdate() {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(updateProductPagePrices, 50);
  }

  // Event listeners
  document.addEventListener('theme:variant:change', debouncedUpdate);
  document.addEventListener('rux:lid:changed', debouncedUpdate);
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.name?.includes('options[') || input.hasAttribute('data-swatch-input')) {
      const productPage = document.querySelector('div.product-page');
      if (productPage && productPage.contains(input)) {
        debouncedUpdate();
      }
    }
  });

  // Also listen for currency/market changes
  document.addEventListener('theme:currency:change', debouncedUpdate);
  document.addEventListener('shopify:section:load', debouncedUpdate);

  // Initial update
  setTimeout(() => {
    console.log('🚀 Running currency-aware price update...');
    updateProductPagePrices();
  }, 500);

  // Make function available for debugging
  window.updateProductPagePrices = updateProductPagePrices;
  window.getCurrentCurrency = getCurrentCurrency;

  console.log('✅ Currency-Aware Price Fix Applied');
  console.log('🔧 Debug: Run getCurrentCurrency() to check detected currency');

})();